// --- Moor200 Client Entry Point ---

import { network } from "./network";
import {
  classifyMeld,
  isNaturalMeld,
  validateAddToMeld,
} from "@moor/shared/index.ts";

// --- Suit helpers ---

const SUIT_SYMBOLS: Record<string, string> = {
  hearts: "\u2665\uFE0E", diamonds: "\u2666\uFE0E", clubs: "\u2663\uFE0E", spades: "\u2660\uFE0E",
};
const SUIT_SORT: Record<string, number> = { spades: 0, hearts: 1, clubs: 2, diamonds: 3 };
const RANK_SORT: Record<string, number> = {
  A: 1, "3": 3, "4": 4, "5": 5, "6": 6, "7": 7,
  "8": 8, "9": 9, "10": 10, J: 11, Q: 12, K: 13, "2": 14, joker: 15,
};
const SEQ_IDX: Record<string, number> = {
  // A=0, rank-2/joker=1 (natural position), 3=2, 4=3, ..., K=12, A-high=13
  A: 0, "3": 2, "4": 3, "5": 4, "6": 5, "7": 6, "8": 7, "9": 8, "10": 9, J: 10, Q: 11, K: 12,
};
const SEQUENCE_MELD_TYPES = new Set([
  "sequence",
  "moor100",
  "moor200",
  "moor1000",
]);

function isRed(suit: string): boolean {
  return suit === "hearts" || suit === "diamonds";
}

// --- State ---

let gameState: any = null;
let mySessionId = "";
let selectedCardIds = new Set<string>();
let selectedMeldId: string | null = null;
let handSortMode: "suit" | "rank" | "manual" = "suit";
let actionPending = false;
let manualHandOrder: string[] = [];
let isSpectator = false;
let selectedGameMode: '4p' | '3p' = '4p';

// Auth state
let authToken: string | null = null;
let authUser: { id: number; username: string; display_name: string } | null = null;

// ─── Push Notification Helpers ─────────────────────────────────────────────

async function registerPushNotifications() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
  try {
    const reg = await navigator.serviceWorker.register('/sw.js');
    // Wait for service worker to be ready
    await navigator.serviceWorker.ready;
    // Check if already subscribed
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      // Request permission
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') return;
      // Get VAPID public key from server
      const { publicKey } = await apiFetch('/api/push/vapid-key');
      const vapidKey = urlBase64ToUint8Array(publicKey);
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapidKey,
      });
    }
    // Send subscription to server
    await apiFetch('/api/push/subscribe', {
      method: 'POST',
      body: JSON.stringify({ subscription: sub.toJSON() }),
    });
  } catch (err) {
    console.warn('Push registration failed:', err);
  }
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Pointer-drag state
let pendingRender = false;
let dragState: {
  active: boolean;
  cardId: string;
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
  ghostEl: HTMLElement | null;
  movedEnough: boolean;
  sourceEl: HTMLElement | null;
} = { active: false, cardId: "", startX: 0, startY: 0, currentX: 0, currentY: 0, ghostEl: null, movedEnough: false, sourceEl: null };

// --- DOM refs ---

const lobbyOverlay = document.getElementById("lobby-overlay")!;
const gameTable = document.getElementById("game-table")!;
const btnCreate = document.getElementById("btn-create") as HTMLButtonElement;
const btnShowJoin = document.getElementById("btn-show-join") as HTMLButtonElement;
const btnJoin = document.getElementById("btn-join") as HTMLButtonElement;
const btnBackLobby = document.getElementById("btn-back-lobby") as HTMLButtonElement;
const nameInput = document.getElementById("player-name") as HTMLInputElement;
const roomCodeInput = document.getElementById("room-code-input") as HTMLInputElement;
const joinForm = document.getElementById("join-form")!;
const lobbyButtons = document.getElementById("lobby-buttons")!;
const waitingArea = document.getElementById("waiting-area")!;
const roomCodeDisplay = document.getElementById("room-code-display")!;
const btnCopyCode = document.getElementById("btn-copy-code")!;
const btnStart = document.getElementById("btn-start") as HTMLButtonElement;
const dragGhostRoot = document.getElementById("drag-ghost")!;
const teamsContainer = document.getElementById("teams-container")!;
const players3pContainer = document.getElementById("players-3p-container")!;
const players3pSlots = document.getElementById("players-3p-slots")!;
const btnMode4p = document.getElementById("btn-mode-4p") as HTMLButtonElement | null;
const btnMode3p = document.getElementById("btn-mode-3p") as HTMLButtonElement | null;

// --- Auth DOM refs ---
const authOverlay = document.getElementById("auth-overlay")!;
const tabLogin = document.getElementById("tab-login")!;
const tabRegister = document.getElementById("tab-register")!;
const formLogin = document.getElementById("form-login")!;
const formRegister = document.getElementById("form-register")!;
const loginUsername = document.getElementById("login-username") as HTMLInputElement;
const loginPassword = document.getElementById("login-password") as HTMLInputElement;
const loginError = document.getElementById("login-error")!;
const btnLogin = document.getElementById("btn-login") as HTMLButtonElement;
const regUsername = document.getElementById("reg-username") as HTMLInputElement;
const regDisplayName = document.getElementById("reg-displayname") as HTMLInputElement;
const regPassword = document.getElementById("reg-password") as HTMLInputElement;
const regError = document.getElementById("reg-error")!;
const btnRegister = document.getElementById("btn-register") as HTMLButtonElement;
const btnLogout = document.getElementById("btn-logout") as HTMLButtonElement;
const lobbyWelcome = document.getElementById("lobby-welcome")!;
const friendsToggle = document.getElementById("friends-toggle")!;
const friendsPanel = document.getElementById("friends-panel")!;
const friendsClose = document.getElementById("friends-close")!;
const friendsSearch = document.getElementById("friends-search") as HTMLInputElement;
const friendsSearchResults = document.getElementById("friends-search-results")!;
const friendsPending = document.getElementById("friends-pending")!;
const friendsList = document.getElementById("friends-list")!;
const friendsBadge = document.getElementById("friends-badge")!;
const fpPendingTitle = document.getElementById("fp-pending-title")!;

// --- API helpers ---

function getApiBase(): string {
  const proto = window.location.protocol;
  const host = window.location.hostname;
  const port = window.location.port;
  const needsPort = port && !["443", "80", ""].includes(port);
  return needsPort ? `${proto}//${host}:${port}` : `${proto}//${host}`;
}

async function apiFetch(path: string, opts: RequestInit = {}): Promise<any> {
  const headers: Record<string, string> = { "Content-Type": "application/json", ...(opts.headers as any || {}) };
  if (authToken) headers["Authorization"] = `Bearer ${authToken}`;
  const res = await fetch(`${getApiBase()}${path}`, { ...opts, headers });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || "خطأ في الخادم");
  return data;
}

// --- Auth Flow ---

const AUTH_TOKEN_KEY = "moor_auth_token";

function saveAuthToken(token: string) {
  authToken = token;
  try { localStorage.setItem(AUTH_TOKEN_KEY, token); } catch {}
}

function loadAuthToken(): string | null {
  try { return localStorage.getItem(AUTH_TOKEN_KEY); } catch { return null; }
}

function clearAuth() {
  authToken = null;
  authUser = null;
  try { localStorage.removeItem(AUTH_TOKEN_KEY); } catch {}
}

async function checkExistingAuth(): Promise<boolean> {
  const token = loadAuthToken();
  if (!token) return false;
  authToken = token;
  try {
    const data = await apiFetch("/api/auth/me");
    authUser = data.user;
    return true;
  } catch {
    clearAuth();
    return false;
  }
}

function showAuthScreen() {
  authOverlay.classList.remove("hidden");
  lobbyOverlay.classList.add("hidden");
  friendsToggle.style.display = "none";
  stopInvitationPolling();
}

function showLobbyScreen() {
  authOverlay.classList.add("hidden");
  lobbyOverlay.classList.remove("hidden");
  friendsToggle.style.display = "flex";
  lobbyWelcome.textContent = `مرحباً ${authUser?.display_name || ""}! — اختر غرفة`;
  // Pre-fill hidden name input with auth display name
  nameInput.value = authUser?.display_name || "";
  // Start polling for invitations while on lobby
  startInvitationPolling();
}

// Tab switching
tabLogin.addEventListener("click", () => {
  tabLogin.classList.add("active"); tabRegister.classList.remove("active");
  formLogin.classList.add("active"); formRegister.classList.remove("active");
});
tabRegister.addEventListener("click", () => {
  tabRegister.classList.add("active"); tabLogin.classList.remove("active");
  formRegister.classList.add("active"); formLogin.classList.remove("active");
});

// Login
btnLogin.addEventListener("click", async () => {
  loginError.textContent = "";
  const username = loginUsername.value.trim();
  const password = loginPassword.value;
  if (!username || !password) { loginError.textContent = "أدخل جميع الحقول"; return; }
  btnLogin.disabled = true;
  try {
    const data = await apiFetch("/api/auth/login", {
      method: "POST", body: JSON.stringify({ username, password }),
    });
    saveAuthToken(data.token);
    authUser = data.user;
    showLobbyScreen();
    loadFriends();
    registerPushNotifications();
  } catch (err: any) {
    loginError.textContent = err.message;
  } finally {
    btnLogin.disabled = false;
  }
});

// Register
btnRegister.addEventListener("click", async () => {
  regError.textContent = "";
  const username = regUsername.value.trim();
  const displayName = regDisplayName.value.trim();
  const password = regPassword.value;
  if (!username || !displayName || !password) { regError.textContent = "أدخل جميع الحقول"; return; }
  btnRegister.disabled = true;
  try {
    const data = await apiFetch("/api/auth/register", {
      method: "POST", body: JSON.stringify({ username, password, displayName }),
    });
    saveAuthToken(data.token);
    authUser = data.user;
    showLobbyScreen();
    loadFriends();
    registerPushNotifications();
  } catch (err: any) {
    regError.textContent = err.message;
  } finally {
    btnRegister.disabled = false;
  }
});

// Logout
btnLogout.addEventListener("click", () => {
  clearAuth();
  network.clearSavedSession();
  showAuthScreen();
});

// Enter key on login/register forms
loginPassword.addEventListener("keydown", (e) => { if (e.key === "Enter") btnLogin.click(); });
regPassword.addEventListener("keydown", (e) => { if (e.key === "Enter") btnRegister.click(); });

// --- Friends Panel ---

friendsToggle.addEventListener("click", () => {
  friendsPanel.classList.toggle("open");
  if (friendsPanel.classList.contains("open")) loadFriends();
});
friendsClose.addEventListener("click", () => friendsPanel.classList.remove("open"));

let friendsSearchTimeout: any = null;
friendsSearch.addEventListener("input", () => {
  clearTimeout(friendsSearchTimeout);
  friendsSearchTimeout = setTimeout(async () => {
    const q = friendsSearch.value.trim();
    if (q.length < 2) { friendsSearchResults.innerHTML = ""; return; }
    try {
      const data = await apiFetch(`/api/friends/search?q=${encodeURIComponent(q)}`);
      friendsSearchResults.innerHTML = data.users.map((u: any) => {
        let actionBtn = "";
        if (u.friend_status === "accepted") {
          actionBtn = `<span class="fp-btn" style="background:rgba(42,125,79,0.2);color:#4caf50;cursor:default;">صديق ✓</span>`;
        } else if (u.friend_status === "pending") {
          actionBtn = `<span class="fp-btn" style="background:rgba(255,215,0,0.1);color:#aaa;cursor:default;">في الانتظار...</span>`;
        } else {
          actionBtn = `<button class="fp-btn add" id="add-btn-${u.id}" onclick="window._addFriend(${u.id})">إضافة +</button>`;
        }
        return `
          <div class="fp-user">
            <div class="fp-user-info">
              <span class="fp-user-name">${escapeHTML(u.display_name)}</span>
              <span class="fp-user-username">@${escapeHTML(u.username)}</span>
            </div>
            <div class="fp-user-actions">
              ${actionBtn}
            </div>
          </div>
        `;
      }).join("");
    } catch {}
  }, 400);
});

(window as any)._addFriend = async (friendId: number) => {
  // Immediately update the button to show pending state
  const btn = document.getElementById(`add-btn-${friendId}`);
  if (btn) {
    btn.outerHTML = `<span class="fp-btn" style="background:rgba(255,215,0,0.1);color:#aaa;cursor:default;">في الانتظار...</span>`;
  }
  try {
    await apiFetch("/api/friends/request", { method: "POST", body: JSON.stringify({ friendId }) });
    showNotification("تم إرسال طلب الصداقة ✓");
    loadFriends();
  } catch (err: any) {
    showNotification(err.message);
  }
};

(window as any)._acceptFriend = async (requesterId: number) => {
  try {
    await apiFetch("/api/friends/accept", { method: "POST", body: JSON.stringify({ requesterId }) });
    showNotification("تم قبول الصداقة ✓");
    loadFriends();
  } catch {}
};

(window as any)._rejectFriend = async (requesterId: number) => {
  try {
    await apiFetch("/api/friends/reject", { method: "POST", body: JSON.stringify({ requesterId }) });
    loadFriends();
  } catch {}
};

(window as any)._removeFriend = async (friendId: number) => {
  try {
    await apiFetch("/api/friends/remove", { method: "POST", body: JSON.stringify({ friendId }) });
    loadFriends();
  } catch {}
};

(window as any)._inviteFriend = async (friendId: number) => {
  const roomCode = roomCodeDisplay.textContent || (gameState as any)?.roomCode || "";
  if (!roomCode) { showNotification("أنشئ غرفة أولاً للدعوة"); return; }
  try {
    await apiFetch("/api/invite", { method: "POST", body: JSON.stringify({ friendId, roomCode }) });
    showNotification("تم ارسال الدعوة سيد راسي ✓");
  } catch (err: any) {
    showNotification(err.message);
  }
};

(window as any)._inviteSpectator = async (friendId: number) => {
  const roomCode = roomCodeDisplay.textContent || (gameState as any)?.roomCode || "";
  if (!roomCode) { showNotification("أنشئ غرفة أولاً للدعوة"); return; }
  try {
    await apiFetch("/api/invite", { method: "POST", body: JSON.stringify({ friendId, roomCode, spectator: true }) });
    showNotification("تم ارسال الدعوة سيد راسي ✓");
  } catch (err: any) {
    showNotification(err.message);
  }
};

// --- Invitation Polling ---

let invitationPollInterval: any = null;
let lastSeenInviteIds = new Set<string>();

function startInvitationPolling() {
  stopInvitationPolling();
  lastSeenInviteIds.clear();
  invitationPollInterval = setInterval(pollInvitations, 5000);
  // Also poll immediately
  pollInvitations();
}

function stopInvitationPolling() {
  if (invitationPollInterval) {
    clearInterval(invitationPollInterval);
    invitationPollInterval = null;
  }
}

async function pollInvitations() {
  if (!authToken) return;
  try {
    const data = await apiFetch("/api/invitations");
    const invites = data.invitations || [];
    for (const inv of invites) {
      if (lastSeenInviteIds.has(inv.id)) continue;
      lastSeenInviteIds.add(inv.id);
      showInvitationPopup(inv);
    }
  } catch {}
}

function showInvitationPopup(inv: { id: string; fromDisplayName: string; roomCode: string; spectator?: boolean }) {
  // If already in a room, don't show invitation popups
  if (network.isConnected()) {
    // Auto-dismiss silently
    apiFetch(`/api/invitations/${inv.id}`, { method: "DELETE" }).catch(() => {});
    return;
  }

  const isSpec = inv.spectator || false;
  const popup = document.createElement("div");
  popup.className = "invitation-popup";
  popup.innerHTML = `
    <div class="inv-text">${isSpec ? "👁️" : "📩"} <b>${escapeHTML(inv.fromDisplayName)}</b> ${isSpec ? "يدعوك للمشاهدة!" : "يدعوك للعب!"}</div>
    <div class="inv-code">كود الغرفة: <b>${escapeHTML(inv.roomCode)}</b></div>
    <div class="inv-actions">
      <button class="inv-btn accept" data-id="${inv.id}" data-code="${escapeHTML(inv.roomCode)}">${isSpec ? "مشاهدة 👁️" : "انضمام 🚀"}</button>
      <button class="inv-btn decline" data-id="${inv.id}">رفض</button>
    </div>
  `;
  document.body.appendChild(popup);
  requestAnimationFrame(() => popup.classList.add("show"));

  // Accept handler
  popup.querySelector(".inv-btn.accept")!.addEventListener("click", async () => {
    popup.remove();
    try {
      await apiFetch(`/api/invitations/${inv.id}`, { method: "DELETE" });
    } catch {}
    const name = authUser?.display_name || "لاعب";
    try {
      if (isSpec) {
        await network.joinRoomAsSpectator(inv.roomCode, name);
      } else {
        await network.joinRoomByCode(inv.roomCode, name);
      }
      showWaitingArea(inv.roomCode);
    } catch (err: any) {
      showNotification(err.message || "فشل في الانضمام");
    }
  });

  // Decline handler
  popup.querySelector(".inv-btn.decline")!.addEventListener("click", async () => {
    popup.classList.remove("show");
    setTimeout(() => popup.remove(), 300);
    try {
      await apiFetch(`/api/invitations/${inv.id}`, { method: "DELETE" });
    } catch {}
  });

  // Auto-dismiss after 30 seconds
  setTimeout(() => {
    if (popup.parentNode) {
      popup.classList.remove("show");
      setTimeout(() => popup.remove(), 300);
    }
  }, 30000);
}

async function loadFriends() {
  if (!authToken) return;
  try {
    const [friendsData, pendingData] = await Promise.all([
      apiFetch("/api/friends"),
      apiFetch("/api/friends/pending"),
    ]);

    // Pending requests
    const received = pendingData.received || [];
    if (received.length > 0) {
      fpPendingTitle.style.display = "block";
      friendsBadge.style.display = "flex";
      friendsBadge.textContent = String(received.length);
      friendsPending.innerHTML = received.map((u: any) => `
        <div class="fp-user">
          <div class="fp-user-info">
            <span class="fp-user-name">${escapeHTML(u.display_name)}</span>
            <span class="fp-user-username">@${escapeHTML(u.username)}</span>
          </div>
          <div class="fp-user-actions">
            <button class="fp-btn accept" onclick="window._acceptFriend(${u.id})">قبول ✓</button>
            <button class="fp-btn reject" onclick="window._rejectFriend(${u.id})">رفض</button>
          </div>
        </div>
      `).join("");
    } else {
      fpPendingTitle.style.display = "none";
      friendsBadge.style.display = "none";
      friendsPending.innerHTML = "";
    }

    // Friends list
    const friends = friendsData.friends || [];
    const roomCode = roomCodeDisplay.textContent || (gameState as any)?.roomCode || "";
    const imAdmin = gameState?.adminSessionId === mySessionId;
    if (friends.length === 0) {
      friendsList.innerHTML = `<div class="fp-empty">لا يوجد أصدقاء بعد — ابحث وأضف!</div>`;
    } else {
      friendsList.innerHTML = friends.map((u: any) => {
        let btns = "";
        if (roomCode) {
          btns += `<button class="fp-btn invite" onclick="window._inviteFriend(${u.id})">دعوة 📩</button>`;
          if (imAdmin) {
            btns += `<button class="fp-btn invite" style="background:rgba(255,215,0,0.12);color:var(--gold);" onclick="window._inviteSpectator(${u.id})">مشاهدة 👁️</button>`;
          }
        }
        btns += `<button class="fp-btn remove" onclick="window._removeFriend(${u.id})">حذف</button>`;
        return `
          <div class="fp-user">
            <div class="fp-user-info">
              <span class="fp-user-name">${escapeHTML(u.display_name)}</span>
              <span class="fp-user-username">@${escapeHTML(u.username)}</span>
            </div>
            <div class="fp-user-actions">
              ${btns}
            </div>
          </div>
        `;
      }).join("");
    }
  } catch {}
}

// --- Lobby ---

// Try auth check + auto-reconnect on page load
(async () => {
  // Step 1: Check if we have a valid auth token
  const hasAuth = await checkExistingAuth();
  
  if (hasAuth) {
    // Step 2: Try reconnecting to an existing room
    registerPushNotifications();
    const saved = network.getSavedSession();
    if (saved) {
      showNotification("جارٍ إعادة الاتصال...");
      const ok = await network.tryReconnect();
      if (ok) {
        showNotification("تم إعادة الاتصال ✓");
        showLobbyScreen(); // ensure lobby is visible (will be hidden by game state)
        return;
      }
      showNotification("فشل إعادة الاتصال — اختر غرفة");
    }
    // Logged in but no active room
    showLobbyScreen();
    loadFriends();
  } else {
    // No auth — show login screen
    showAuthScreen();
  }
})();

// Handle disconnect
network.onDisconnect = (code: number) => {
  showNotification("⚠ انقطع الاتصال — جارٍ المحاولة...");
  // Auto-retry reconnection every 3 seconds
  let attempts = 0;
  const retryInterval = setInterval(async () => {
    attempts++;
    if (attempts > 20) { // 60 seconds max
      clearInterval(retryInterval);
      showNotification("فشل إعادة الاتصال. أعد تحميل الصفحة.");
      return;
    }
    const ok = await network.tryReconnect();
    if (ok) {
      clearInterval(retryInterval);
      showNotification("تم إعادة الاتصال ✓");
    }
  }, 3000);
};

if (btnMode4p) {
  btnMode4p.addEventListener("click", () => {
    selectedGameMode = '4p';
    btnMode4p.classList.add("active");
    btnMode3p?.classList.remove("active");
  });
}
if (btnMode3p) {
  btnMode3p.addEventListener("click", () => {
    selectedGameMode = '3p';
    btnMode3p.classList.add("active");
    btnMode4p?.classList.remove("active");
  });
}

btnCreate.addEventListener("click", async () => {
  const name = authUser?.display_name || nameInput.value.trim() || "\u0644\u0627\u0639\u0628";
  try {
    btnCreate.disabled = true;
    if (selectedGameMode === '3p') {
      await network.createRoom3p(name);
    } else {
      await network.createRoom(name);
    }
    showWaitingArea("");
  } catch (err) {
    alert("\u0641\u0634\u0644 \u0641\u064a \u0625\u0646\u0634\u0627\u0621 \u0627\u0644\u063a\u0631\u0641\u0629");
    btnCreate.disabled = false;
  }
});

btnShowJoin.addEventListener("click", () => {
  lobbyButtons.style.display = "none";
  joinForm.style.display = "block";
  roomCodeInput.focus();
});

btnBackLobby.addEventListener("click", () => {
  joinForm.style.display = "none";
  lobbyButtons.style.display = "block";
});

// --- Available Rooms ---

const btnAvailableRooms = document.getElementById("btn-available-rooms") as HTMLButtonElement;
const availableRoomsPanel = document.getElementById("available-rooms-panel")!;
const availableRoomsList = document.getElementById("available-rooms-list")!;
const btnBackFromRooms = document.getElementById("btn-back-from-rooms") as HTMLButtonElement;

let availableRoomsRefreshTimer: any = null;

btnAvailableRooms.addEventListener("click", async () => {
  lobbyButtons.style.display = "none";
  availableRoomsPanel.style.display = "block";
  await loadAvailableRooms();
  // Auto-refresh every 5 seconds while panel is open
  availableRoomsRefreshTimer = setInterval(loadAvailableRooms, 5000);
});

btnBackFromRooms.addEventListener("click", () => {
  clearInterval(availableRoomsRefreshTimer);
  availableRoomsRefreshTimer = null;
  availableRoomsPanel.style.display = "none";
  lobbyButtons.style.display = "block";
});

async function loadAvailableRooms() {
  try {
    const data = await apiFetch("/api/rooms/available");
    const rooms = data.rooms || [];
    if (rooms.length === 0) {
      availableRoomsList.innerHTML = `<div class="avail-empty">لا توجد غرف متاحة حالياً</div>`;
      return;
    }
    availableRoomsList.innerHTML = rooms.map((r: any) => {
      const playerNames = r.players.map((p: any) => escapeHTML(p.displayName)).join("، ");
      const maxPlayers = r.maxPlayers || 4;
      const modeLabel = r.gameMode === "3p" ? "3 لاعبين" : "4 لاعبين";
      const modeColor = r.gameMode === "3p" ? "color:#a78bfa" : "color:var(--gold)";
      const isLobby = r.phase === "lobby";
      const statusLabel = isLobby ? "⏳ في الانتظار" : "🎮 جارية";
      const statusColor = isLobby ? "color:#4ade80" : "color:#fb923c";
      return `
        <div class="avail-room-card" onclick="window._joinAvailableRoom('${escapeHTML(r.roomCode)}')">
          <div style="display:flex;align-items:center;gap:8px;justify-content:center;">
            <div class="avail-room-code">${escapeHTML(r.roomCode)}</div>
            <span style="font-size:10px;font-weight:700;${modeColor};background:rgba(255,255,255,0.05);padding:2px 7px;border-radius:8px;">${modeLabel}</span>
          </div>
          <div class="avail-room-info" style="${statusColor}">${statusLabel} — ${r.playerCount}/${maxPlayers} لاعبين</div>
          <div class="avail-room-players">${playerNames || "فارغة"}</div>
        </div>
      `;
    }).join("");
  } catch {
    availableRoomsList.innerHTML = `<div class="avail-empty">فشل في تحميل الغرف</div>`;
  }
}

(window as any)._joinAvailableRoom = async (roomCode: string) => {
  const name = authUser?.display_name || nameInput.value.trim() || "لاعب";
  try {
    await network.joinRoomByCode(roomCode, name);
    availableRoomsPanel.style.display = "none";
    showWaitingArea(roomCode);
  } catch (err: any) {
    showNotification(err.message || "فشل في الانضمام");
  }
};

btnJoin.addEventListener("click", async () => {
  const name = authUser?.display_name || nameInput.value.trim() || "\u0644\u0627\u0639\u0628";
  const code = roomCodeInput.value.trim().toUpperCase();
  if (!code || code.length < 4) {
    alert("أدخل كود الغرفة");
    return;
  }
  try {
    btnJoin.disabled = true;
    await network.joinRoomByCode(code, name);
    showWaitingArea(code);
  } catch (err: any) {
    alert(err.message || "\u0641\u0634\u0644 \u0641\u064a \u0627\u0644\u0627\u0646\u0636\u0645\u0627\u0645");
    btnJoin.disabled = false;
  }
});

function showWaitingArea(roomCode: string) {
  lobbyButtons.style.display = "none";
  joinForm.style.display = "none";
  nameInput.style.display = "none";
  waitingArea.style.display = "block";
  if (roomCode) {
    roomCodeDisplay.textContent = roomCode;
  }
  btnCopyCode.onclick = () => {
    const code = roomCodeDisplay.textContent || "";
    navigator.clipboard.writeText(code);
    btnCopyCode.textContent = "\u062a\u0645 \u0627\u0644\u0646\u0633\u062e! \u2713";
    setTimeout(() => (btnCopyCode.textContent = "نسخ الكود 📋"), 2000);
  };
}

function resetToLobby() {
  // Leave room and go back to lobby
  network.leaveRoom();
  isSpectator = false;
  gameState = null;
  selectedCardIds.clear();
  selectedMeldId = null;
  manualHandOrder = [];
  waitingArea.style.display = "none";
  joinForm.style.display = "none";
  nameInput.style.display = "none";
  availableRoomsPanel.style.display = "none";
  lobbyButtons.style.display = "block";
  btnCreate.disabled = false;
  btnJoin.disabled = false;
  gameTable.classList.remove("active");
  lobbyOverlay.classList.remove("hidden");
  // Stop available rooms refresh if open
  clearInterval(availableRoomsRefreshTimer);
  availableRoomsRefreshTimer = null;
  availableRoomsPanel.style.display = "none";
  // Clean up in-game elements
  document.getElementById("btn-leave-game")?.remove();
  document.getElementById("spectator-badge")?.remove();
  document.getElementById("spectator-count")?.remove();
  const _topScores = document.querySelector(".top-scores") as HTMLElement;
  if (_topScores) { _topScores.classList.remove("has-history"); _topScores.onclick = null; _topScores.title = ""; _topScores.querySelector(".history-indicator")?.remove(); }
  document.getElementById("hand-area")!.style.display = "";
  // Reset render fingerprints so re-entering a game renders fresh
  Object.keys(_lastHash).forEach(k => delete _lastHash[k]);
  // Reset 3p UI state
  teamsContainer.style.display = '';
  players3pContainer.style.display = 'none';
  selectedGameMode = '4p';
  if (btnMode4p) { btnMode4p.classList.add('active'); }
  if (btnMode3p) { btnMode3p.classList.remove('active'); }
  startInvitationPolling();
}

// Leave room button handler
(window as any)._leaveRoom = () => {
  resetToLobby();
  showNotification("تم الخروج من الغرفة");
};

function updatePlayerSlots(stateOrPlayers: any) {
  const state = Array.isArray(stateOrPlayers)
    ? { players: stateOrPlayers, gameMode: '4p', adminSessionId: null }
    : stateOrPlayers;
  const players: any[] = state.players;

  if (state.gameMode === '3p') {
    teamsContainer.style.display = 'none';
    players3pContainer.style.display = '';
    players3pSlots.innerHTML = '';
    for (let seat = 0; seat < 3; seat++) {
      const player = players.find((p: any) => p.seatIndex === seat);
      const slot = document.createElement('div');
      slot.className = `player-slot${player ? ' filled' : ''}`;
      if (player) {
        slot.innerHTML = `<span>${escapeHTML(player.displayName)}</span>`;
      } else {
        slot.innerHTML = `<span style="color:#445;">\u0641\u064a \u0627\u0646\u062a\u0638\u0627\u0631...</span>`;
      }
      players3pSlots.appendChild(slot);
    }
    return;
  }

  teamsContainer.style.display = '';
  players3pContainer.style.display = 'none';

  const teamASlots = document.getElementById("team-a-slots")!;
  const teamBSlots = document.getElementById("team-b-slots")!;
  teamASlots.innerHTML = "";
  teamBSlots.innerHTML = "";

  const isAdmin = state.adminSessionId === mySessionId;

  // Team A = seats 0, 2; Team B = seats 1, 3
  const teamASeats = [0, 2];
  const teamBSeats = [1, 3];

  function renderSlot(seat: number, container: HTMLElement) {
    const player = players.find((p: any) => p.seatIndex === seat);
    const slot = document.createElement("div");
    slot.className = `player-slot ${player ? "filled" : ""}`;
    if (player) {
      const isBot = player.isBot;
      slot.innerHTML = `<span>${isBot ? "🤖 " : ""}${escapeHTML(player.displayName)}</span>`;
      // Admin can swap this player to the other team
      if (isAdmin && players.length >= 2 && !isBot) {
        const btn = document.createElement("button");
        btn.className = "swap-btn";
        btn.title = "نقل للفريق الآخر";
        btn.textContent = "⇄";
        btn.addEventListener("click", () => {
          // Find a player on the other team to swap with
          const otherTeamSeats = seat % 2 === 0 ? teamBSeats : teamASeats;
          const otherPlayer = otherTeamSeats.map(s => players.find((p: any) => p.seatIndex === s)).find(p => p);
          if (otherPlayer) {
            network.sendAdminReorder(seat, otherPlayer.seatIndex);
          }
        });
        slot.appendChild(btn);
      }
      // Admin can remove a bot
      if (isAdmin && isBot) {
        const btnRemove = document.createElement("button");
        btnRemove.className = "swap-btn";
        btnRemove.title = "إزالة البوت";
        btnRemove.textContent = "✕";
        btnRemove.style.color = "#ff6b6b";
        btnRemove.addEventListener("click", () => network.sendRemoveBot(seat));
        slot.appendChild(btnRemove);
      }
    } else {
      slot.innerHTML = `<span style="color:#445;">في انتظار...</span>`;
      // Admin can add a bot to empty seat
      if (isAdmin) {
        const btnBot = document.createElement("button");
        btnBot.className = "swap-btn";
        btnBot.title = "إضافة بوت";
        btnBot.textContent = "🤖";
        btnBot.addEventListener("click", () => network.sendAddBot());
        slot.appendChild(btnBot);
      }
    }
    container.appendChild(slot);
  }

  for (const seat of teamASeats) renderSlot(seat, teamASlots);
  for (const seat of teamBSeats) renderSlot(seat, teamBSlots);
}

btnStart.addEventListener("click", () => network.sendStartGame());

// --- State Updates ---

network.onJoined = (data: any) => {
  if (data.roomCode) {
    roomCodeDisplay.textContent = data.roomCode;
  }
  isSpectator = data.spectator || false;
  if (isSpectator) {
    showNotification("أنت تشاهد كمتفرج 👁️");
  }
};

// Handle server-side leave confirmation
network.onLeft = (_data: any) => {
  resetToLobby();
  showNotification("تم الخروج من الغرفة");
};

// Handle being kicked
network.onKicked = (_data: any) => {
  resetToLobby();
  showNotification("تم إخراجك من الغرفة");
};

// Leave game button handler (works during gameplay)
(window as any)._leaveGame = () => {
  if (confirm("هل أنت متأكد من الخروج من اللعبة؟")) {
    network.sendLeaveGame();
    resetToLobby();
    showNotification("تم الخروج من الغرفة");
  }
};

network.onStateUpdate = (state: any) => {
  actionPending = false; // Re-enable actions on every state update
  gameState = state;
  mySessionId = network.getSessionId();

  if (state.roomCode && roomCodeDisplay.textContent !== state.roomCode) {
    roomCodeDisplay.textContent = state.roomCode;
  }

  if (state.phase === "lobby") {
    showWaitingArea(state.roomCode || "");
    updatePlayerSlots(state);
    const isAdmin = state.adminSessionId === mySessionId;
    const requiredPlayers = state.gameMode === '3p' ? 3 : 4;
    const ready = state.players.length >= requiredPlayers;
    btnStart.disabled = !ready || !isAdmin;
    if (!isAdmin) {
      btnStart.textContent = ready ? "\u0628\u0627\u0646\u062a\u0638\u0627\u0631 \u0627\u0644\u0645\u0633\u0624\u0648\u0644 \u0644\u0628\u062f\u0621 \u0627\u0644\u0644\u0639\u0628\u0629..." : `\u0641\u064a \u0627\u0646\u062a\u0638\u0627\u0631 \u0627\u0644\u0644\u0627\u0639\u0628\u064a\u0646 (${state.players.length}/${requiredPlayers})...`;
    } else {
      btnStart.textContent = ready ? "\u0628\u062f\u0621 \u0627\u0644\u0644\u0639\u0628\u0629! \ud83c\udfae" : `\u0641\u064a \u0627\u0646\u062a\u0638\u0627\u0631 \u0627\u0644\u0644\u0627\u0639\u0628\u064a\u0646 (${state.players.length}/${requiredPlayers})...`;
    }
  }

  if (state.phase !== "lobby") {
    lobbyOverlay.classList.add("hidden");
    authOverlay.classList.add("hidden");
    friendsToggle.style.display = "flex";
    gameTable.classList.add("active");
    stopInvitationPolling();

    // Show/hide leave game button in top bar
    let leaveBtn = document.getElementById("btn-leave-game");
    if (!leaveBtn) {
      leaveBtn = document.createElement("button");
      leaveBtn.id = "btn-leave-game";
      leaveBtn.className = "leave-game-btn";
      leaveBtn.textContent = "🚪";
      leaveBtn.title = "خروج من الغرفة";
      leaveBtn.onclick = () => (window as any)._leaveGame();
      document.getElementById("top-bar")!.appendChild(leaveBtn);
    }

    // Show spectator badge
    let specBadge = document.getElementById("spectator-badge");
    if (isSpectator) {
      if (!specBadge) {
        specBadge = document.createElement("div");
        specBadge.id = "spectator-badge";
        specBadge.className = "spectator-badge";
        specBadge.textContent = "👁️ مشاهد";
        document.getElementById("top-bar")!.appendChild(specBadge);
      }
      // Hide hand area for spectators
      document.getElementById("hand-area")!.style.display = "none";
    } else {
      if (specBadge) specBadge.remove();
      document.getElementById("hand-area")!.style.display = "";
    }

    // Show spectator count if any
    const spectators = state.spectators || [];
    let specCount = document.getElementById("spectator-count");
    if (spectators.length > 0) {
      if (!specCount) {
        specCount = document.createElement("div");
        specCount.id = "spectator-count";
        specCount.className = "spectator-count";
        document.getElementById("top-bar")!.appendChild(specCount);
      }
      specCount.textContent = `👁️ ${spectators.length}`;
      specCount.title = spectators.map((s: any) => s.displayName).join("، ");
    } else if (specCount) {
      specCount.remove();
    }

    // Show history indicator on top-scores when there are previous round scores
    const topScoresEl = document.querySelector(".top-scores") as HTMLElement;
    if (topScoresEl) {
      if (state.roundScores && state.roundScores.length > 0 && state.phase === "playing") {
        if (!topScoresEl.classList.contains("has-history")) {
          topScoresEl.classList.add("has-history");
          topScoresEl.onclick = () => showLastRoundResults();
          topScoresEl.title = "انقر لعرض نتيجة الجولة السابقة";
          if (!topScoresEl.querySelector(".history-indicator")) {
            const ind = document.createElement("span");
            ind.className = "history-indicator";
            ind.textContent = "📊";
            topScoresEl.appendChild(ind);
          }
        }
      } else {
        topScoresEl.classList.remove("has-history");
        topScoresEl.onclick = null;
        topScoresEl.title = "";
        topScoresEl.querySelector(".history-indicator")?.remove();
      }
    }

    const me = state.players.find((p: any) => p.sessionId === mySessionId);
    if (me) syncManualOrder(me.hand);
    // Don't re-render the full game while user is actively dragging a card
    if (!dragState.active) {
      renderGame();
    } else {
      pendingRender = true;
    }
  }
};

network.onGameEvent = (data: any) => showNotification(translateEvent(data.event, data));
network.onError = (data: any) => { actionPending = false; showNotification(`\u26a0 ${data.message}`); };

// ─── Chat ───────────────────────────────────────────────────────────────────
let chatUnread = 0;

network.onChat = (data: any) => {
  appendChatMessage(data);
  // Show floating bubble notification if chat panel is closed
  const panel = document.getElementById("chat-panel")!;
  const isMine = data.seatIndex !== undefined && gameState?.players?.find((p: any) => p.sessionId === mySessionId)?.seatIndex === data.seatIndex;
  if (!isMine) {
    showChatBubble(data);
  }
  if (!panel.classList.contains("open")) {
    chatUnread++;
    const badge = document.getElementById("chat-badge")!;
    badge.style.display = "flex";
    badge.textContent = chatUnread > 9 ? "9+" : String(chatUnread);
  }
};

function appendChatMessage(data: any) {
  const container = document.getElementById("chat-messages")!;
  const isMine = data.seatIndex !== undefined && gameState?.players?.find((p: any) => p.sessionId === mySessionId)?.seatIndex === data.seatIndex;
  const div = document.createElement("div");
  div.className = `chat-msg team-${data.teamId || 'A'}${isMine ? ' mine' : ''}`;
  const time = new Date(data.timestamp).toLocaleTimeString("ar", { hour: "2-digit", minute: "2-digit" });
  div.innerHTML = `<div class="chat-name">${escapeHTML(data.sender || '')}</div><div class="chat-text">${escapeHTML(data.text || '')}</div><div class="chat-time">${time}</div>`;
  container.appendChild(div);
  container.scrollTop = container.scrollHeight;
  // Keep max 100 messages
  while (container.children.length > 100) container.removeChild(container.firstChild!);
}

function escapeHTML(str: string): string {
  const d = document.createElement("div");
  d.textContent = str;
  return d.innerHTML;
}

function showChatBubble(data: any) {
  const container = document.getElementById("chat-bubbles")!;
  const bubble = document.createElement("div");
  bubble.className = "chat-bubble";
  const teamClass = `team-${data.teamId || 'A'}`;
  bubble.innerHTML = `<div class="bubble-name ${teamClass}">${escapeHTML(data.sender || '')}</div><div class="bubble-text">${escapeHTML(data.text || '')}</div>`;
  container.appendChild(bubble);
  // Remove after animation completes (4s total: 3.6s visible + 0.4s fade)
  setTimeout(() => {
    if (bubble.parentNode) bubble.parentNode.removeChild(bubble);
  }, 4000);
  // Keep max 3 bubbles visible
  while (container.children.length > 3) {
    container.removeChild(container.firstChild!);
  }
}

function sendChatMessage() {
  const input = document.getElementById("chat-input") as HTMLInputElement;
  const text = input.value.trim();
  if (!text) return;
  network.sendChat(text);
  input.value = "";
  input.focus();
}

document.getElementById("chat-send")!.addEventListener("click", sendChatMessage);
document.getElementById("chat-input")!.addEventListener("keydown", (e) => {
  if (e.key === "Enter") { e.preventDefault(); sendChatMessage(); }
  e.stopPropagation(); // Don't trigger game shortcuts
});
// Reset unread when opening panel
document.getElementById("chat-toggle")!.addEventListener("click", () => {
  chatUnread = 0;
  setTimeout(() => {
    const container = document.getElementById("chat-messages")!;
    container.scrollTop = container.scrollHeight;
    document.getElementById("chat-input")!.focus();
  }, 50);
});

// --- Manual Order Sync ---

let newlyDrawnCardId: string | null = null;
let lastMeldCardCounts: Record<string, number> = {};
let lastModifiedMeldId: string | null = null;
let lastModifiedMeldTimer: ReturnType<typeof setTimeout> | null = null;

function syncManualOrder(hand: any[]) {
  const visibleCards = hand.filter((c: any) => !c.hidden && c.rank && c.suit);
  const currentIds = new Set(visibleCards.map((c: any) => c.id));
  
  // Detect new cards — stays highlighted until player taps any card
  const oldIds = new Set(manualHandOrder);
  for (const card of visibleCards) {
    if (!oldIds.has(card.id)) {
      newlyDrawnCardId = card.id;
    }
  }

  manualHandOrder = manualHandOrder.filter(id => currentIds.has(id));
  for (const card of visibleCards) {
    if (!manualHandOrder.includes(card.id)) {
      manualHandOrder.push(card.id);
    }
  }
}

// --- Render fingerprinting (avoid unnecessary DOM rebuilds) ---
const _lastHash: Record<string, string> = {};
function _fp(key: string, data: any): boolean {
  const h = JSON.stringify(data);
  if (_lastHash[key] === h) return false; // no change
  _lastHash[key] = h;
  return true; // changed
}

// --- Render Game ---

function renderGame() {
  if (!gameState) return;
  const state = gameState;
  const me = state.players.find((p: any) => p.sessionId === mySessionId);
  if (!me) return;

  if (state.gameMode === '3p') {
    renderGame3p(state, me);
    return;
  }

  const isMyTurn = state.currentTurnSeat === me.seatIndex;
  const phase = state.phase;

  // Top bar
  document.getElementById("score-a")!.textContent = state.teamATotalScore;
  document.getElementById("score-b")!.textContent = state.teamBTotalScore;
  document.getElementById("round-info")!.textContent = `\u0627\u0644\u062c\u0648\u0644\u0629 ${state.roundNumber}`;

  const currentPlayer = state.players.find((p: any) => p.seatIndex === state.currentTurnSeat);
  const turnInfoEl = document.getElementById("turn-info")!;
  if (phase === "playing") {
    turnInfoEl.textContent = isMyTurn
      ? "\u2605 \u062f\u0648\u0631\u0643!" : `\u062f\u0648\u0631: ${currentPlayer?.displayName || "..."}`;
    turnInfoEl.className = isMyTurn ? "my-turn" : "";
  } else if (phase === "round-end") {
    turnInfoEl.textContent = "\u0627\u0646\u062a\u0647\u062a \u0627\u0644\u062c\u0648\u0644\u0629";
    turnInfoEl.className = "";
  } else {
    turnInfoEl.textContent = "";
    turnInfoEl.className = "";
  }

  if (_fp("opponents", { players: state.players.map((p: any) => ({ s: p.seatIndex, n: p.displayName, c: p.isConnected })), turn: state.currentTurnSeat, admin: state.adminSessionId })) {
    renderOpponents(state, me);
  }
  document.getElementById("draw-count")!.textContent = state.drawPile?.length || 0;
  if (_fp("discard", state.centerDiscard?.map((c: any) => c.id))) {
    renderDiscardPile(state.centerDiscard);
  }
  document.getElementById("discard-count")!.textContent = state.centerDiscard?.length || 0;

  // Side pile badges
  const sideABadge = document.getElementById("side-a-badge")!;
  const sideBBadge = document.getElementById("side-b-badge")!;
  if (state.sidePileATaken) {
    sideABadge.textContent = `✓ ${state.sidePileATeam || ""}`;
    sideABadge.classList.add("taken");
  } else {
    sideABadge.textContent = "11 أ";
    sideABadge.classList.remove("taken");
  }
  if (state.sidePileBTaken) {
    sideBBadge.textContent = `✓ ${state.sidePileBTeam || ""}`;
    sideBBadge.classList.add("taken");
  } else {
    sideBBadge.textContent = "11 ب";
    sideBBadge.classList.remove("taken");
  }

  // Detect which meld was modified (cards added) — for scroll+flash
  if (state.melds) {
    for (const meld of state.melds) {
      const prev = lastMeldCardCounts[meld.id];
      if (prev !== undefined && meld.cards.length > prev) {
        if (lastModifiedMeldTimer) clearTimeout(lastModifiedMeldTimer);
        lastModifiedMeldId = meld.id;
        lastModifiedMeldTimer = setTimeout(() => {
          lastModifiedMeldId = null;
          lastModifiedMeldTimer = null;
          scrolledToMeldId = null;
          // Remove flash class directly — NO renderGame() to avoid scroll reset
          document.querySelectorAll(".meld-flash").forEach(el => el.classList.remove("meld-flash"));
          // Invalidate melds fingerprint so next real state update re-renders correctly
          _lastHash["melds"] = "";
        }, 3500);
      }
      lastMeldCardCounts[meld.id] = meld.cards.length;
    }
  }

  if (_fp("melds", { m: state.melds?.map((m: any) => ({ id: m.id, t: m.meldType, c: m.cards.length })), sel: selectedMeldId, flash: lastModifiedMeldId })) {
    renderMelds(state.melds, me);
  }
  if (_fp("hand", { h: me.hand?.map((c: any) => c.id), sel: [...selectedCardIds].sort(), drawn: newlyDrawnCardId })) {
    renderHand(me.hand);
  }
  updateButtons(state, me, isMyTurn);

  // Side pile chooser
  const spc = document.getElementById("side-pile-chooser")!;
  if (phase === "choosing-side-pile" && state.currentTurnSeat === me.seatIndex) {
    spc.classList.remove("hidden");
    (document.getElementById("btn-side-a") as HTMLButtonElement).disabled = state.sidePileATaken;
    (document.getElementById("btn-side-b") as HTMLButtonElement).disabled = state.sidePileBTaken;
  } else {
    spc.classList.add("hidden");
  }

  // Round end — show score breakdown
  const reOverlay = document.getElementById("round-end-overlay")!;
  if (phase === "round-end") {
    reOverlay.classList.remove("hidden");
    renderRoundEndScores(state, me);
  } else {
    reOverlay.classList.add("hidden");
  }

  // Game over
  const goOverlay = document.getElementById("game-over-overlay")!;
  if (phase === "game-over") {
    goOverlay.classList.remove("hidden");
    const isWinner = state.winner === me.teamId;
    document.getElementById("game-over-text")!.textContent = isWinner ? "\ud83c\udf89 \u0641\u0631\u064a\u0642\u0643 \u0641\u0627\u0632! \ud83c\udf89" : "\ud83d\ude14 \u0641\u0631\u064a\u0642\u0643 \u062e\u0633\u0631";
    document.getElementById("final-scores")!.textContent =
      `\u0627\u0644\u0641\u0631\u064a\u0642 \u0623: ${state.teamATotalScore} | \u0627\u0644\u0641\u0631\u064a\u0642 \u0628: ${state.teamBTotalScore}`;
  } else {
    goOverlay.classList.add("hidden");
  }
}

// --- 3-Player Game Render ---

function renderGame3p(state: any, me: any) {
  const isMyTurn = state.currentTurnSeat === me.seatIndex;
  const phase = state.phase;

  // Top bar: show my score in score-a, hide score-b
  const myScore = state.playerTotalScores?.[me.seatIndex] ?? 0;
  document.getElementById("score-a")!.textContent = String(myScore);
  document.getElementById("score-b")!.textContent = "";
  document.getElementById("round-info")!.textContent = `\u0627\u0644\u062c\u0648\u0644\u0629 ${state.roundNumber}`;

  const currentPlayer = state.players.find((p: any) => p.seatIndex === state.currentTurnSeat);
  const turnInfoEl = document.getElementById("turn-info")!;
  if (phase === "playing") {
    turnInfoEl.textContent = isMyTurn ? "\u2605 \u062f\u0648\u0631\u0643!" : `\u062f\u0648\u0631: ${currentPlayer?.displayName || "..."}`;
    turnInfoEl.className = isMyTurn ? "my-turn" : "";
  } else if (phase === "round-end") {
    turnInfoEl.textContent = "\u0627\u0646\u062a\u0647\u062a \u0627\u0644\u062c\u0648\u0644\u0629";
    turnInfoEl.className = "";
  } else {
    turnInfoEl.textContent = "";
    turnInfoEl.className = "";
  }

  if (_fp("opponents3p", { players: state.players.map((p: any) => ({ s: p.seatIndex, n: p.displayName, c: p.isConnected })), turn: state.currentTurnSeat, solo: state.soloPlayerSeat })) {
    renderOpponents3p(state, me);
  }

  document.getElementById("draw-count")!.textContent = state.drawPile?.length || 0;
  if (_fp("discard", state.centerDiscard?.map((c: any) => c.id))) {
    renderDiscardPile(state.centerDiscard);
  }
  document.getElementById("discard-count")!.textContent = state.centerDiscard?.length || 0;

  // Side pile badges
  const sideABadge = document.getElementById("side-a-badge")!;
  const sideBBadge = document.getElementById("side-b-badge")!;
  if (state.sidePileATaken) {
    const takenBy = state.players.find((p: any) => p.seatIndex === state.sidePileATakenBy);
    sideABadge.textContent = `\u2713 ${takenBy?.displayName || ""}`;
    sideABadge.classList.add("taken");
  } else {
    sideABadge.textContent = "11 \u0623";
    sideABadge.classList.remove("taken");
  }
  if (state.sidePileBTaken) {
    const takenBy = state.players.find((p: any) => p.seatIndex === state.sidePileBTakenBy);
    sideBBadge.textContent = `\u2713 ${takenBy?.displayName || ""}`;
    sideBBadge.classList.add("taken");
  } else {
    sideBBadge.textContent = "11 \u0628";
    sideBBadge.classList.remove("taken");
  }

  // Track meld modifications for flash/scroll
  if (state.melds) {
    for (const meld of state.melds) {
      const prev = lastMeldCardCounts[meld.id];
      if (prev !== undefined && meld.cards.length > prev) {
        if (lastModifiedMeldTimer) clearTimeout(lastModifiedMeldTimer);
        lastModifiedMeldId = meld.id;
        lastModifiedMeldTimer = setTimeout(() => {
          lastModifiedMeldId = null;
          lastModifiedMeldTimer = null;
          scrolledToMeldId = null;
          document.querySelectorAll(".meld-flash").forEach(el => el.classList.remove("meld-flash"));
          _lastHash["melds3p"] = "";
        }, 3500);
      }
      lastMeldCardCounts[meld.id] = meld.cards.length;
    }
  }

  if (_fp("melds3p", { m: state.melds?.map((m: any) => ({ id: m.id, t: m.meldType, c: m.cards.length, o: m.ownerSeat })), sel: selectedMeldId, flash: lastModifiedMeldId })) {
    renderMelds3p(state, me);
  }

  if (_fp("hand", { h: me.hand?.map((c: any) => c.id), sel: [...selectedCardIds].sort(), drawn: newlyDrawnCardId })) {
    renderHand(me.hand);
  }

  updateButtons(state, me, isMyTurn);

  // Side pile chooser
  const spc = document.getElementById("side-pile-chooser")!;
  if (phase === "choosing-side-pile" && state.currentTurnSeat === me.seatIndex) {
    spc.classList.remove("hidden");
    (document.getElementById("btn-side-a") as HTMLButtonElement).disabled = state.sidePileATaken;
    (document.getElementById("btn-side-b") as HTMLButtonElement).disabled = state.sidePileBTaken;
  } else {
    spc.classList.add("hidden");
  }

  // Round end
  const reOverlay = document.getElementById("round-end-overlay")!;
  if (phase === "round-end") {
    reOverlay.classList.remove("hidden");
    renderRoundEndScores(state, me);
  } else {
    reOverlay.classList.add("hidden");
  }

  // Game over
  const goOverlay = document.getElementById("game-over-overlay")!;
  if (phase === "game-over") {
    goOverlay.classList.remove("hidden");
    const winnerSeat = state.winner;
    const isWinner = winnerSeat === me.seatIndex;
    const winnerPlayer = state.players.find((p: any) => p.seatIndex === winnerSeat);
    document.getElementById("game-over-text")!.textContent = isWinner
      ? "\ud83c\udf89 \u0623\u0646\u062a \u0627\u0644\u0641\u0627\u0626\u0632! \ud83c\udf89"
      : `\ud83d\ude14 \u0627\u0644\u0641\u0627\u0626\u0632: ${winnerPlayer?.displayName || "..."}`;
    const scoresText = state.players
      .map((p: any) => `${p.displayName}: ${state.playerTotalScores?.[p.seatIndex] ?? 0}`)
      .join(" | ");
    document.getElementById("final-scores")!.textContent = scoresText;
  } else {
    goOverlay.classList.add("hidden");
  }
}

function renderOpponents3p(state: any, me: any) {
  const row = document.getElementById("opponents-row")!;
  row.innerHTML = "";
  for (let offset = 1; offset <= 2; offset++) {
    const seat = (me.seatIndex + offset) % 3;
    const p = state.players.find((pl: any) => pl.seatIndex === seat);
    if (!p) continue;
    const isTurn = state.currentTurnSeat === seat;
    const isDisconnected = !p.isConnected;
    const isSolo = state.soloPlayerSeat === seat;
    const isPartner = state.partnerSeats?.includes(seat);
    const roleLabel = isSolo ? " \ud83d\udc51" : isPartner ? " \ud83e\udd1d" : "";
    const box = document.createElement("div");
    box.className = `opponent-box${isTurn ? " is-turn" : ""}${isDisconnected ? " disconnected" : ""}`;
    const posLabel = offset === 1 ? "\u064a\u0645\u064a\u0646" : "\u064a\u0633\u0627\u0631";
    let fanHTML = '<div class="opp-cards-fan">';
    for (let i = 0; i < 5; i++) fanHTML += '<div class="opponent-mini-card"></div>';
    fanHTML += "</div>";
    const disconnectBadge = isDisconnected ? '<span style="color:#ff5252; font-size:10px;">\u26a0 \u063a\u064a\u0631 \u0645\u062a\u0635\u0644</span>' : "";
    box.innerHTML = `
      <div class="opp-avatar">${escapeHTML(avatarInitial(p.displayName))}</div>
      <div class="opp-text">
        <div class="opp-name">${escapeHTML(p.displayName)}${roleLabel}</div>
        <div class="opp-info">${posLabel} ${disconnectBadge}</div>
      </div>
      ${fanHTML}
    `;
    row.appendChild(box);
  }
}

// --- Opponents ---

/** First visible letter of a name, for the seat avatar. */
function avatarInitial(name: string): string {
  const ch = Array.from((name || "").trim())[0];
  return ch || "?";
}

function renderOpponents(state: any, me: any) {
  const row = document.getElementById("opponents-row")!;
  row.innerHTML = "";
  const partnerSeat = (me.seatIndex + 2) % 4;
  const isAdmin = state.adminSessionId === mySessionId;

  for (let offset = 1; offset <= 3; offset++) {
    const seat = (me.seatIndex + offset) % 4;
    const p = state.players.find((pl: any) => pl.seatIndex === seat);
    if (!p) continue;

    const isPartner = seat === partnerSeat;
    const isTurn = state.currentTurnSeat === seat;
    const isDisconnected = !p.isConnected;
    const box = document.createElement("div");
    box.className = `opponent-box${isPartner ? " partner" : ""}${isTurn ? " is-turn" : ""}${isDisconnected ? " disconnected" : ""}`;
    const posLabel = offset === 1 ? "\u064a\u0645\u064a\u0646" : offset === 2 ? "\u0634\u0631\u064a\u0643" : "\u064a\u0633\u0627\u0631";

    // Mini card fan — show fixed number of card backs (don't reveal count)
    let fanHTML = '<div class="opp-cards-fan">';
    const fanSize = Math.min(5, 5); // Fixed 5 card backs, never reveal actual count
    for (let i = 0; i < fanSize; i++) {
      fanHTML += '<div class="opponent-mini-card"></div>';
    }
    fanHTML += '</div>';

    const disconnectBadge = isDisconnected ? '<span style="color:#ff5252; font-size:10px;">⚠ غير متصل</span>' : '';

    box.innerHTML = `
      <div class="opp-avatar">${escapeHTML(avatarInitial(p.displayName))}</div>
      <div class="opp-text">
        <div class="opp-name">${escapeHTML(p.displayName)}</div>
        <div class="opp-info">${posLabel} ${disconnectBadge}</div>
      </div>
      ${fanHTML}
    `;
    row.appendChild(box);
  }
}

// --- Discard Pile ---

function renderDiscardPile(cards: any[]) {
  const container = document.getElementById("discard-cards")!;
  container.innerHTML = "";
  // Empty pile: the dashed slot shows through. Otherwise only the TOP card is visible;
  // the pile size is shown by the #discard-count badge.
  if (!cards || cards.length === 0) return;
  const topCard = cards[cards.length - 1];
  if (topCard && !topCard.hidden) {
    container.appendChild(createMiniCardEl(topCard));
  }
}

// --- Melds ---

const MELD_LABELS: Record<string, string> = {
  sequence: "تسلسل",
  moor100: "مور100",
  moor200: "مور200",
  moor1000: "مور1000",
};

function canCreateMeldFromHand(state: any, me: any, handCards: any[]): boolean {
  if (handCards.length < 3) return false;
  // Check if any 3+ card subset forms a valid sequence
  for (let i = 0; i < handCards.length - 2; i++) {
    for (let j = i + 1; j < handCards.length - 1; j++) {
      for (let k = j + 1; k < handCards.length; k++) {
        const cards = [handCards[i], handCards[j], handCards[k]];
        const classification = classifyMeld(cards);
        if (!classification) continue;
        // First meld must be natural if not yet melded natural this turn
        if (!state.hasMeldedNaturalThisTurn && !isNaturalMeld(cards)) continue;
        return true;
      }
    }
  }
  return false;
}

function canAddToTeamMeldFromHand(state: any, me: any, handCards: any[]): boolean {
  if (!state?.melds?.length || handCards.length === 0) return false;
  const teamMelds = state.melds.filter((m: any) => m.teamId === me.teamId);

  for (const meld of teamMelds) {
    for (const card of handCards) {
      const classification = validateAddToMeld(meld, [card]);
      if (!classification) continue;
      return true;
    }
  }
  return false;
}

/**
 * Moor200: can take center only if top card can be used in a sequence
 * that the player can immediately lay down that turn.
 * Returns true if the action is available (server will do full validation).
 */
function canTakeCenter(state: any, me: any): boolean {
  const topCard = getTopCenterCard(state);
  if (!topCard) return false;
  // Initial center card: any card can be taken — no meld required
  if (state.isInitialCenterCard) return true;
  // Grace turn (draw pile exhausted): ONLY a natural sequence with the top card qualifies
  const isGraceTurn = state.drawPileExhaustedSeat !== null && state.drawPileExhaustedSeat !== undefined;
  const handCards: any[] = me?.hand || [];

  // Sequences require all non-joker cards to share the same suit.
  // Pre-filter the pool to same-suit cards + jokers so we never try cross-suit combos.
  // This avoids exponential blowup when the player holds many cards (e.g. 26 cards → ~10 relevant).
  const seqPool: any[] = topCard.isJoker
    ? [...handCards, topCard]
    : [...handCards, topCard].filter((c: any) => c.isJoker || c.suit === topCard.suit);

  const maxSize = Math.min(seqPool.length, 14);
  for (let size = 3; size <= maxSize; size++) {
    if (combosIncludingCard(seqPool, topCard, size, (cards: any[]) => {
      const cl = classifyMeld(cards);
      if (!cl) return false;
      if (isGraceTurn && !isNaturalMeld(cards)) return false;
      // Same as the server: only a player who has not opened yet must lead with a natural sequence
      const mustBeNatural = !state.hasMeldedNaturalThisTurn && !(state.openedSeats || []).includes(me?.seatIndex);
      if (!isGraceTurn && mustBeNatural && !isNaturalMeld(cards)) return false;
      return true;
    })) return true;
  }
  return false;
}

/** Check if any combination of `size` elements from `arr` that includes `required` passes `test`. */
function combosIncludingCard(arr: any[], required: any, size: number, test: (cards: any[]) => boolean): boolean {
  const others = arr.filter((c: any) => c.id !== required.id);
  function pick(start: number, current: any[]): boolean {
    if (current.length === size - 1) {
      return test([...current, required]);
    }
    for (let i = start; i < others.length; i++) {
      current.push(others[i]);
      if (pick(i + 1, current)) return true;
      current.pop();
    }
    return false;
  }
  return pick(0, []);
}

/** Get the visible top card of the center discard pile */
function getTopCenterCard(state: any): any {
  if (!state?.centerDiscard?.length) return null;
  return state.centerDiscard[state.centerDiscard.length - 1];
}

function isValidSequencePlacement(rawIndices: number[], jokerCount: number, totalCards: number): boolean {
  if (rawIndices.length === 0) return false;
  const indices = [...rawIndices].sort((a, b) => a - b);

  for (let i = 1; i < indices.length; i++) {
    if (indices[i] === indices[i - 1]) return false;
  }

  const minIdx = indices[0];
  const maxIdx = indices[indices.length - 1];
  const span = maxIdx - minIdx + 1;
  const gaps = span - indices.length;
  if (gaps > jokerCount) return false;
  if (span > totalCards) return false;

  const extraJokers = jokerCount - gaps;
  const totalLength = span + extraJokers;
  return totalLength === totalCards && totalLength <= 14;
}

function shouldDisplayAceHigh(cards: any[]): boolean {
  const nonJokers = cards.filter((c: any) => !c.isJoker);
  if (!nonJokers.some((c: any) => c.rank === "A")) return false;

  const jokerCount = cards.length - nonJokers.length;
  const lowIndices = nonJokers.map((c: any) => SEQ_IDX[c.rank]).filter((i: any) => i !== undefined);
  const highIndices = nonJokers.map((c: any) => c.rank === "A" ? 13 : SEQ_IDX[c.rank]).filter((i: any) => i !== undefined);
  const lowValid = isValidSequencePlacement(lowIndices, jokerCount, cards.length);
  const highValid = isValidSequencePlacement(highIndices, jokerCount, cards.length);

  // Only display ace-high if A-low placement is NOT valid (always prefer A-low)
  return highValid && !lowValid;
}

function arrangeSequenceWithJokers(cards: any[]): any[] {
  const allJokers = cards.filter((c: any) => c.isJoker);
  const nonJokers = cards.filter((c: any) => !c.isJoker);

  if (nonJokers.length === 0) return cards;

  const sequenceSuit = nonJokers[0]?.suit;

  const aces = nonJokers.filter((c: any) => c.rank === "A");
  const hasMixedAces = aces.length >= 2;
  const aceHigh = !hasMixedAces && aces.length === 1 && shouldDisplayAceHigh(cards);

  const sameSuitJokers = allJokers.filter((j: any) => j.suit === sequenceSuit);
  const diffSuitJokers = allJokers.filter((j: any) => j.suit !== sequenceSuit);

  function buildBaseMap(): Map<number, any> {
    const m = new Map<number, any>();
    let aceCount = 0;
    for (const c of nonJokers) {
      let idx: number;
      if (c.rank === "A") {
        if (hasMixedAces) { idx = aceCount === 0 ? 0 : 13; aceCount++; }
        else { idx = aceHigh ? 13 : 0; }
      } else {
        idx = SEQ_IDX[c.rank] ?? 99;
      }
      m.set(idx, c);
    }
    return m;
  }

  function tryArrange(useNaturalAtIdx1: boolean): any[] | null {
    const naturalJoker = useNaturalAtIdx1 && sameSuitJokers.length > 0 ? sameSuitJokers[0] : null;
    const subJokers = naturalJoker
      ? [...sameSuitJokers.slice(1), ...diffSuitJokers]
      : [...sameSuitJokers, ...diffSuitJokers];

    const idxToCard = buildBaseMap();
    if (naturalJoker) {
      if (idxToCard.has(1)) return null; // conflict with a real card at rank-2 position
      idxToCard.set(1, naturalJoker);
    }

    const allFixed = [...idxToCard.keys()].sort((a: number, b: number) => a - b);
    if (allFixed.length === 0) return null;

    const minIdx = allFixed[0];
    const maxIdx = allFixed[allFixed.length - 1];

    const arranged: any[] = [];
    let subIdx = 0;

    for (let i = minIdx; i <= maxIdx; i++) {
      if (idxToCard.has(i)) {
        arranged.push(idxToCard.get(i));
      } else if (subIdx < subJokers.length) {
        arranged.push(subJokers[subIdx++]);
      } else {
        return null; // unfilled gap — this arrangement doesn't work
      }
    }

    const suffix: any[] = [];
    const prefix: any[] = [];
    while (subIdx < subJokers.length) {
      const nextUpIdx = maxIdx + suffix.length + 1;
      if (nextUpIdx <= 13) {
        suffix.push(subJokers[subIdx++]);
      } else {
        prefix.unshift(subJokers[subIdx++]);
      }
    }

    const result = [...prefix, ...arranged, ...suffix];
    if (result.length !== cards.length) return null;
    return result;
  }

  // Prefer same-suit joker at natural index 1 (rank-2 position) —
  // mirrors the server's tryBuildNormalized priority.
  if (sameSuitJokers.length > 0) {
    const withNatural = tryArrange(true);
    if (withNatural) return withNatural;
  }

  // Fall back: treat all jokers as substitutes
  const withoutNatural = tryArrange(false);
  if (withoutNatural) return withoutNatural;

  return cards; // last resort: return as-is
}

function sortMeldCardsForDisplay(meld: any): any[] {
  const cards = [...(meld?.cards || [])];
  if (cards.length <= 1) return cards;

  if (SEQUENCE_MELD_TYPES.has(meld.meldType)) {
    return arrangeSequenceWithJokers(cards);
  }

  return cards.sort((a: any, b: any) => {
    const rankDiff = (RANK_SORT[a.rank] ?? 99) - (RANK_SORT[b.rank] ?? 99);
    if (rankDiff !== 0) return rankDiff;
    const suitDiff = (SUIT_SORT[a.suit] ?? 99) - (SUIT_SORT[b.suit] ?? 99);
    if (suitDiff !== 0) return suitDiff;
    return a.id.localeCompare(b.id);
  });
}

// Keep the player's place in the meld zones across re-renders: re-rendering rebuilds
// the lanes, which would otherwise snap every lane back to its first meld.
let scrolledToMeldId: string | null = null;
const MELD_ZONE_IDS = ["melds-opp", "melds-mine"];

type MeldScroll = { zones: Record<string, number>; lanes: Record<string, number> };

function captureMeldScroll(): MeldScroll {
  const saved: MeldScroll = { zones: {}, lanes: {} };
  for (const id of MELD_ZONE_IDS) {
    const zone = document.getElementById(id);
    if (zone) saved.zones[id] = zone.scrollTop;
  }
  document.querySelectorAll<HTMLElement>(".meld-team-scroll[data-lane]").forEach((el) => {
    saved.lanes[el.dataset.lane!] = el.scrollLeft;
  });
  return saved;
}

function restoreMeldScroll(saved: MeldScroll) {
  document.querySelectorAll<HTMLElement>(".meld-team-scroll[data-lane]").forEach((el) => {
    const left = saved.lanes[el.dataset.lane!];
    if (left !== undefined) el.scrollLeft = left;
  });
  for (const id of MELD_ZONE_IDS) {
    const zone = document.getElementById(id);
    if (zone && saved.zones[id] !== undefined) zone.scrollTop = saved.zones[id];
  }
}

/** Bring a just-changed meld into view — only once, and only for the player's own move. */
function maybeScrollToMeld(group: HTMLElement, meldId: string, isMyMove: boolean) {
  if (!isMyMove || scrolledToMeldId === meldId) return;
  scrolledToMeldId = meldId;
  requestAnimationFrame(() => {
    group.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  });
}

function meldCountLabel(n: number): string {
  if (n === 1) return "سلسلة واحدة";
  if (n === 2) return "سلسلتان";
  if (n <= 10) return `${n} سلاسل`;
  return `${n} سلسلة`;
}

function isMoorType(meldType: string): boolean {
  return meldType === "moor100" || meldType === "moor200" || meldType === "moor1000";
}

/** One lane: a title (name, count, show-all) and a sideways-scrolling two-row tray. */
function buildMeldLane(opts: {
  laneKey: string; title: string; laneClass: string; melds: any[]; isMyMove: boolean;
}): HTMLElement {
  const lane = document.createElement("div");
  lane.className = `meld-team-lane team-${opts.laneClass}`;

  const title = document.createElement("div");
  title.className = `meld-team-title team-${opts.laneClass}`;
  const name = document.createElement("span");
  name.textContent = opts.title;
  title.appendChild(name);
  if (opts.melds.length > 0) {
    const count = document.createElement("span");
    count.className = "mt-count";
    count.textContent = meldCountLabel(opts.melds.length);
    title.appendChild(count);
    const all = document.createElement("button");
    all.className = "mt-all";
    all.textContent = "عرض الكل";
    all.onclick = (e) => { e.stopPropagation(); openMeldsSheet(opts.laneKey); };
    title.appendChild(all);
  }
  lane.appendChild(title);

  const scroll = document.createElement("div");
  scroll.className = "meld-team-scroll";
  scroll.dataset.lane = opts.laneKey;

  if (opts.melds.length === 0) {
    const empty = document.createElement("div");
    empty.className = "meld-team-empty";
    empty.textContent = "لا توجد سلاسل بعد";
    scroll.appendChild(empty);
  }
  for (const meld of opts.melds) {
    const group = document.createElement("div");
    group.className = "meld-group";

    const label = document.createElement("div");
    label.className = `meld-label team-${opts.laneClass}${isMoorType(meld.meldType) ? " moor" : ""}`;
    label.textContent = MELD_LABELS[meld.meldType] || meld.meldType;
    group.appendChild(label);

    const cardsRow = document.createElement("div");
    cardsRow.className = `meld-cards${selectedMeldId === meld.id ? " selected" : ""}`;
    cardsRow.onclick = () => {
      selectedMeldId = selectedMeldId === meld.id ? null : meld.id;
      renderGame();
    };
    for (const card of sortMeldCardsForDisplay(meld)) {
      cardsRow.appendChild(createMiniCardEl(card));
    }
    group.appendChild(cardsRow);
    scroll.appendChild(group);

    if (meld.id === lastModifiedMeldId) {
      cardsRow.classList.add("meld-flash");
      maybeScrollToMeld(group, meld.id, opts.isMyMove);
    }
  }
  lane.appendChild(scroll);
  return lane;
}

function renderMelds(melds: any[], me: any) {
  const saved = captureMeldScroll();
  const oppZone = document.getElementById("melds-opp")!;
  const mineZone = document.getElementById("melds-mine")!;
  oppZone.innerHTML = "";
  mineZone.innerHTML = "";
  oppZone.classList.remove("multi");
  mineZone.classList.remove("multi");

  const myTeam: "A" | "B" = me?.teamId === "B" ? "B" : "A";
  const oppTeam: "A" | "B" = myTeam === "A" ? "B" : "A";
  const isMyMove = gameState?.currentTurnSeat === me?.seatIndex;
  const all = melds || [];

  oppZone.appendChild(buildMeldLane({
    laneKey: `team-${oppTeam}`, title: "سلاسل الخصم", laneClass: oppTeam.toLowerCase(),
    melds: all.filter((m: any) => m.teamId === oppTeam), isMyMove,
  }));
  mineZone.appendChild(buildMeldLane({
    laneKey: `team-${myTeam}`, title: "سلاسل فريقنا", laneClass: myTeam.toLowerCase(),
    melds: all.filter((m: any) => m.teamId === myTeam), isMyMove,
  }));

  restoreMeldScroll(saved);
  fitMeldZones();
  refreshMeldsSheet();
}

// --- Melds 3p ---

function renderMelds3p(state: any, me: any) {
  const saved = captureMeldScroll();
  const oppZone = document.getElementById("melds-opp")!;
  const mineZone = document.getElementById("melds-mine")!;
  oppZone.innerHTML = "";
  mineZone.innerHTML = "";
  // Two opponents share the top zone, so each gets a single-row tray
  oppZone.classList.add("multi");
  mineZone.classList.remove("multi");

  const isMyMove = state.currentTurnSeat === me.seatIndex;
  const seatsToShow: number[] = [me.seatIndex, (me.seatIndex + 1) % 3, (me.seatIndex + 2) % 3];
  for (const seat of seatsToShow) {
    const p = state.players.find((pl: any) => pl.seatIndex === seat);
    if (!p) continue;
    const isOwn = seat === me.seatIndex;
    const isSolo = state.soloPlayerSeat === seat;
    const isPartner = state.partnerSeats?.includes(seat);
    const roleSuffix = isSolo ? " 👑" : isPartner ? " 🤝" : "";
    const lane = buildMeldLane({
      laneKey: `seat-${seat}`, title: `${p.displayName}${roleSuffix}`, laneClass: isOwn ? "a" : "b",
      melds: (state.melds || []).filter((m: any) => m.ownerSeat === seat), isMyMove,
    });
    (isOwn ? mineZone : oppZone).appendChild(lane);
  }

  restoreMeldScroll(saved);
  fitMeldZones();
  refreshMeldsSheet();
}

/**
 * Keep every meld inside the table: melds wrap onto as many rows as the zone holds, and
 * when they still don't fit, the meld cards shrink step by step (or grow when there's room).
 * Only below the smallest readable size does the zone fall back to scrolling.
 */
const MELD_FIT_SCALES = [1.3, 1.18, 1.08, 1, 0.92, 0.85, 0.78, 0.72, 0.66, 0.6, 0.55];

function fitMeldZones() {
  const root = getComputedStyle(document.documentElement);
  const baseW = parseFloat(root.getPropertyValue("--zone-card-w")) || 30;
  const baseH = parseFloat(root.getPropertyValue("--zone-card-h")) || 43;
  const baseOverlap = parseFloat(root.getPropertyValue("--zone-overlap")) || -15;

  for (const id of MELD_ZONE_IDS) {
    const zone = document.getElementById(id);
    if (!zone) continue;
    zone.classList.remove("overflowing");
    const lanes = Array.from(zone.querySelectorAll<HTMLElement>(".meld-team-lane"));
    const fits = () => lanes.every((lane) => lane.scrollHeight <= lane.clientHeight + 1) &&
      zone.scrollHeight <= zone.clientHeight + 1;
    let fitted = false;
    for (const scale of MELD_FIT_SCALES) {
      zone.style.setProperty("--zone-card-w", `${(baseW * scale).toFixed(1)}px`);
      zone.style.setProperty("--zone-card-h", `${(baseH * scale).toFixed(1)}px`);
      zone.style.setProperty("--zone-overlap", `${(baseOverlap * scale).toFixed(1)}px`);
      if (fits()) { fitted = true; break; }
    }
    if (!fitted) zone.classList.add("overflowing");
  }
}

let meldFitTimer: ReturnType<typeof setTimeout> | null = null;
window.addEventListener("resize", () => {
  if (meldFitTimer) clearTimeout(meldFitTimer);
  meldFitTimer = setTimeout(fitMeldZones, 120);
});

// --- Show-all melds sheet ---

let meldsSheetKey: string | null = null;

/** The meld groups the sheet can switch between, for the current game mode. */
function meldsSheetGroups(): { key: string; label: string; title: string; melds: any[]; mine: boolean }[] {
  const state = gameState;
  const me = getMe();
  if (!state || !me) return [];
  const melds = state.melds || [];
  if (state.gameMode === "3p") {
    return [me.seatIndex, (me.seatIndex + 1) % 3, (me.seatIndex + 2) % 3].map((seat: number) => {
      const p = state.players.find((pl: any) => pl.seatIndex === seat);
      const mine = seat === me.seatIndex;
      const label = mine ? "سلاسلي" : (p?.displayName || "");
      return { key: `seat-${seat}`, label, title: mine ? "سلاسلي" : `سلاسل ${label}`,
        melds: melds.filter((m: any) => m.ownerSeat === seat), mine };
    });
  }
  const myTeam = me.teamId === "B" ? "B" : "A";
  const oppTeam = myTeam === "A" ? "B" : "A";
  return [
    { key: `team-${myTeam}`, label: "فريقنا", title: "سلاسل فريقنا",
      melds: melds.filter((m: any) => m.teamId === myTeam), mine: true },
    { key: `team-${oppTeam}`, label: "الخصم", title: "سلاسل الخصم",
      melds: melds.filter((m: any) => m.teamId === oppTeam), mine: false },
  ];
}

function openMeldsSheet(key: string) {
  meldsSheetKey = key;
  document.getElementById("melds-sheet")!.classList.remove("hidden");
  refreshMeldsSheet();
}

function closeMeldsSheet() {
  meldsSheetKey = null;
  document.getElementById("melds-sheet")!.classList.add("hidden");
}

function refreshMeldsSheet() {
  if (!meldsSheetKey) return;
  const groups = meldsSheetGroups();
  const group = groups.find((g) => g.key === meldsSheetKey) || groups[0];
  if (!group) {
    closeMeldsSheet();
    return;
  }

  const title = document.getElementById("ms-title")!;
  title.textContent = group.title;
  title.className = group.mine ? "" : "opp";
  document.getElementById("ms-count")!.textContent = meldCountLabel(group.melds.length);

  const tabs = document.getElementById("ms-tabs")!;
  tabs.innerHTML = "";
  for (const g of groups) {
    const b = document.createElement("button");
    b.textContent = g.label;
    if (g.key === group.key) b.className = "on";
    b.onclick = () => { meldsSheetKey = g.key; refreshMeldsSheet(); };
    tabs.appendChild(b);
  }

  // Tapping one of your own melds selects it as the target for "إضافة"
  document.getElementById("ms-hint")!.textContent = group.mine
    ? "اضغط على سلسلة لتحديدها، ثم اضغط «إضافة»"
    : "";

  const list = document.getElementById("ms-list")!;
  list.innerHTML = "";
  for (const meld of group.melds) {
    const row = document.createElement(group.mine ? "button" : "div");
    row.className = `ms-row${selectedMeldId === meld.id ? " sel" : ""}`;
    const meta = document.createElement("div");
    meta.className = "ms-meta";
    const kind = document.createElement("b");
    kind.textContent = MELD_LABELS[meld.meldType] || meld.meldType;
    if (isMoorType(meld.meldType)) kind.className = "moor";
    const size = document.createElement("small");
    size.textContent = `${meld.cards.length} أوراق`;
    meta.append(kind, size);
    const cardsEl = document.createElement("div");
    cardsEl.className = "ms-cards";
    for (const card of sortMeldCardsForDisplay(meld)) cardsEl.appendChild(createMiniCardEl(card));
    row.append(meta, cardsEl);
    if (group.mine) {
      row.onclick = () => {
        selectedMeldId = meld.id;
        closeMeldsSheet();
        renderGame();
      };
    }
    list.appendChild(row);
  }
}

document.getElementById("ms-close")!.addEventListener("click", closeMeldsSheet);
document.querySelector("#melds-sheet .ms-scrim")!.addEventListener("click", closeMeldsSheet);

// --- Hand ---

function renderHand(hand: any[]) {
  const container = document.getElementById("hand-cards")!;
  container.innerHTML = "";
  // Clear any leftover drag states
  document.querySelectorAll(".drag-gap").forEach(el => el.classList.remove("drag-gap"));
  if (!hand || hand.length === 0) return;

  // Filter out hidden/malformed cards
  const visible = hand.filter((c: any) => !c.hidden && c.rank && c.suit);
  let ordered: any[];

  if (handSortMode === "manual") {
    const cardMap = new Map(visible.map((c: any) => [c.id, c]));
    ordered = manualHandOrder
      .filter(id => cardMap.has(id))
      .map(id => cardMap.get(id)!);
  } else {
    ordered = sortHand(visible);
  }

  // Layout: up to FAN_MAX cards sit in one fanned row; larger hands (after taking a big
  // pile) are split into balanced rows so every card stays visible without scrolling.
  const scrollEl = document.getElementById("hand-cards-scroll")!;
  const availWidth = Math.max(200, scrollEl.clientWidth - 16);
  const fanAllowance = ordered.length <= FAN_MAX ? 44 : 0; // rotated end cards swing outward
  const cardW = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--card-w')) || 62;
  const rowCount = handRowCount(ordered.length, availWidth, cardW);
  const perRow = Math.ceil(ordered.length / rowCount);
  const rows: any[][] = [];
  for (let r = 0; r < rowCount; r++) rows.push(ordered.slice(r * perRow, (r + 1) * perRow));
  // Overlap so the widest row fits; never less than a sliver of each card stays visible
  const fitOverlap = perRow > 1 ? (availWidth - fanAllowance - perRow * cardW) / (perRow - 1) : 0;
  const overlap = Math.max(-cardW * 0.68, Math.min(-6, fitOverlap));
  const fan = rowCount === 1 && ordered.length > 1;

  for (const rowCards of rows) {
    const rowEl = rowCount > 1 ? document.createElement("div") : container;
    if (rowCount > 1) rowEl.className = "hand-row";
    rowCards.forEach((card, i) => {
      const el = createHandCardEl(card);
      // In RTL, the first element (rightmost) sits on top of the next one
      el.style.zIndex = (rowCards.length - i).toString();
      // RTL: the overlap goes on the end (left) side, between this card and the next one.
      // A negative start-side margin would push the first card off the edge of the screen.
      el.style.marginRight = "0";
      el.style.marginLeft = i < rowCards.length - 1 ? `${overlap}px` : "0";
      if (fan) {
        // Gentle arc: up to ±12° at the ends, outer cards dip a little
        const mid = (rowCards.length - 1) / 2;
        const t = mid > 0 ? (i - mid) / mid : 0;
        el.style.setProperty("--fan", `${(-t * Math.min(6, rowCards.length * 0.6)).toFixed(2)}deg`);
        el.style.setProperty("--fan-y", `${(t * t * 6).toFixed(1)}px`);
      }

      if (selectedCardIds.has(card.id)) el.classList.add("selected");
      if (newlyDrawnCardId === card.id) el.classList.add("new-card");

      // If this card is being dragged AND actually moved, add dragging class
      if (dragState.active && dragState.movedEnough && dragState.cardId === card.id) {
        el.classList.add("dragging");
      }

      // Pointer-based drag (long-press on touch, immediate on mouse)
      el.addEventListener("pointerdown", (e: PointerEvent) => {
        if (e.button !== 0) return; // only primary button
        const isTouch = e.pointerType === "touch";
        if (isTouch) {
          // On touch: start a long-press timer; don't preventDefault yet (allow scroll)
          startLongPressDrag(card.id, e.clientX, e.clientY, el, e.pointerId);
        } else {
          // On mouse: immediate drag start
          e.preventDefault();
          startDrag(card.id, e.clientX, e.clientY, el);
        }
      });

      // Prevent iOS Safari text-selection callout on long-press
      el.addEventListener("contextmenu", (e: Event) => e.preventDefault());

      rowEl.appendChild(el);
    });
    if (rowEl !== container) container.appendChild(rowEl);
  }

  // Multi-row hand for large hands
  const handArea = document.getElementById("hand-area")!;
  handArea.classList.toggle("two-row", rowCount > 1);
}

const FAN_MAX = 14;

/** How many rows a hand of `n` cards needs so each card keeps at least ~32% visible. */
function handRowCount(n: number, availWidth: number, cardW: number): number {
  if (n <= FAN_MAX) return 1;
  const minStep = cardW * 0.32;
  const maxPerRow = Math.max(2, Math.floor((availWidth - cardW) / minStep) + 1);
  return Math.max(2, Math.ceil(n / maxPerRow));
}

// ====== POINTER DRAG SYSTEM (RTL-aware) ======

const DRAG_THRESHOLD = 8; // px movement before drag activates
const LONG_PRESS_MS = 350; // ms for touch long-press to activate drag
let dragScrollInterval: number | null = null;
let dragRafPending = false;
let longPressTimer: number | null = null;
let longPressState: { cardId: string; x: number; y: number; el: HTMLElement; pointerId: number } | null = null;

function cancelLongPress() {
  if (longPressTimer !== null) {
    clearTimeout(longPressTimer);
    longPressTimer = null;
  }
  longPressState = null;
}

function startLongPressDrag(cardId: string, x: number, y: number, el: HTMLElement, pointerId: number) {
  cancelLongPress();
  longPressState = { cardId, x, y, el, pointerId };

  // Track movement during long-press window — if user scrolls, cancel
  const onLpMove = (e: PointerEvent) => {
    if (!longPressState) return;
    const dx = Math.abs(e.clientX - longPressState.x);
    const dy = Math.abs(e.clientY - longPressState.y);
    if (dx > 10 || dy > 10) {
      // User is scrolling, cancel long-press
      cancelLongPress();
      document.removeEventListener("pointermove", onLpMove);
      document.removeEventListener("pointerup", onLpUp);
      document.removeEventListener("pointercancel", onLpUp);
    }
  };
  const onLpUp = () => {
    cancelLongPress();
    document.removeEventListener("pointermove", onLpMove);
    document.removeEventListener("pointerup", onLpUp);
    document.removeEventListener("pointercancel", onLpUp);
    // Clear drawn-card highlight once player interacts with their hand
    newlyDrawnCardId = null;
    // It was a tap — toggle selection
    if (selectedCardIds.has(cardId)) {
      selectedCardIds.delete(cardId);
    } else {
      selectedCardIds.add(cardId);
    }
    renderGame();
  };

  document.addEventListener("pointermove", onLpMove, { passive: true });
  document.addEventListener("pointerup", onLpUp);
  document.addEventListener("pointercancel", onLpUp);

  longPressTimer = window.setTimeout(() => {
    if (!longPressState) return;
    // Long press activated — remove LP listeners and start real drag
    document.removeEventListener("pointermove", onLpMove);
    document.removeEventListener("pointerup", onLpUp);
    document.removeEventListener("pointercancel", onLpUp);

    // Haptic feedback if available
    if (navigator.vibrate) navigator.vibrate(30);

    // Capture pointer to prevent browser scroll from interfering with drag
    try { el.setPointerCapture(longPressState.pointerId); } catch {}

    // Visual feedback: pulse the card
    el.style.transform = "translateY(-18px) scale(1.12)";
    el.style.borderColor = "var(--gold)";

    startDrag(cardId, longPressState.x, longPressState.y, el);
    // Immediately set movedEnough so the ghost appears
    dragState.movedEnough = true;
    if (handSortMode !== "manual") {
      snapshotCurrentOrder();
      handSortMode = "manual";
    }
    createDragGhost(cardId);
    if (dragState.sourceEl) dragState.sourceEl.classList.add("dragging");
    startAutoScroll();

    longPressState = null;
    longPressTimer = null;
  }, LONG_PRESS_MS);
}

function startDrag(cardId: string, x: number, y: number, el: HTMLElement) {
  dragState = {
    active: true,
    cardId,
    startX: x,
    startY: y,
    currentX: x,
    currentY: y,
    ghostEl: null,
    movedEnough: false,
    sourceEl: el,
  };
  document.addEventListener("pointermove", onPointerMove, { passive: false });
  document.addEventListener("pointerup", onPointerUp);
  document.addEventListener("pointercancel", onPointerUp);
}

function onPointerMove(e: PointerEvent) {
  if (!dragState.active) return;
  e.preventDefault();

  dragState.currentX = e.clientX;
  dragState.currentY = e.clientY;

  const dx = Math.abs(e.clientX - dragState.startX);
  const dy = Math.abs(e.clientY - dragState.startY);

  if (!dragState.movedEnough && (dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD)) {
    dragState.movedEnough = true;

    // Switch to manual mode on first drag
    if (handSortMode !== "manual") {
      snapshotCurrentOrder();
      handSortMode = "manual";
    }

    // Create floating ghost card
    createDragGhost(dragState.cardId);

    // Mark source card as dragging
    if (dragState.sourceEl) {
      dragState.sourceEl.classList.add("dragging");
    }

    // Start auto-scroll near edges
    startAutoScroll();
  }

  if (dragState.movedEnough && dragState.ghostEl) {
    // Move ghost to follow cursor using rAF for smooth animation
    if (!dragRafPending) {
      dragRafPending = true;
      requestAnimationFrame(() => {
        dragRafPending = false;
        if (dragState.ghostEl) {
          dragState.ghostEl.style.left = `${dragState.currentX}px`;
          dragState.ghostEl.style.top = `${dragState.currentY}px`;
        }
        updateDropIndicator(dragState.currentX, dragState.currentY);
      });
    }
  }
}

function onPointerUp(e: PointerEvent) {
  if (!dragState.active) return;

  cancelLongPress(); // safety: cancel any lingering long-press
  document.removeEventListener("pointermove", onPointerMove);
  document.removeEventListener("pointerup", onPointerUp);
  document.removeEventListener("pointercancel", onPointerUp);
  stopAutoScroll();

  if (dragState.movedEnough) {
    performDrop(e.clientX, e.clientY);
    removeDragGhost();
    clearDragGap();
    dragState = { active: false, cardId: "", startX: 0, startY: 0, currentX: 0, currentY: 0, ghostEl: null, movedEnough: false, sourceEl: null };
    renderGame();
  } else {
    // It was a tap/click — toggle selection
    const cardId = dragState.cardId;
    // Clear drawn-card highlight when player interacts with their hand
    newlyDrawnCardId = null;
    if (selectedCardIds.has(cardId)) {
      selectedCardIds.delete(cardId);
    } else {
      selectedCardIds.add(cardId);
    }
    dragState = { active: false, cardId: "", startX: 0, startY: 0, currentX: 0, currentY: 0, ghostEl: null, movedEnough: false, sourceEl: null };
    renderGame();
  }

  // If a state update arrived during drag, render now
  if (pendingRender) {
    pendingRender = false;
    renderGame();
  }
}

function createDragGhost(cardId: string) {
  const me = getMe();
  if (!me) return;
  const card = me.hand.find((c: any) => c.id === cardId);
  if (!card) return;

  const ghost = dragGhostRoot;
  ghost.innerHTML = "";
  const clone = createHandCardEl(card);
  clone.style.width = "100%";
  clone.style.height = "100%";
  clone.style.margin = "0";
  clone.style.pointerEvents = "none";
  ghost.appendChild(clone);
  ghost.classList.add("visible");
  dragState.ghostEl = ghost;
}

function removeDragGhost() {
  dragGhostRoot.classList.remove("visible");
  dragGhostRoot.innerHTML = "";
  dragState.ghostEl = null;
}

/**
 * Get non-dragging cards sorted in visual RTL order (rightmost first = array index 0).
 * In RTL flex, DOM order = manualHandOrder order = visual right-to-left.
 */
type HandSlot = { id: string; centerX: number; centerY: number; row: number };

/** Hand cards in reading order: row by row (top first), each row right-to-left. */
function getVisualCardPositions(): HandSlot[] {
  const handCards = document.getElementById("hand-cards")!;
  const rowEls = Array.from(handCards.querySelectorAll<HTMLElement>(":scope > .hand-row"));
  const items: HandSlot[] = [];
  handCards.querySelectorAll<HTMLElement>("[data-card-id]").forEach((child) => {
    const cid = child.dataset.cardId;
    if (!cid || cid === dragState.cardId) return;
    const rect = child.getBoundingClientRect();
    const row = rowEls.length ? Math.max(0, rowEls.indexOf(child.parentElement as HTMLElement)) : 0;
    items.push({ id: cid, centerX: rect.left + rect.width / 2, centerY: rect.top + rect.height / 2, row });
  });
  items.sort((a, b) => a.row - b.row || b.centerX - a.centerX);
  return items;
}

/**
 * Find the array insertion index for pointer position (x, y).
 * Picks the hand row nearest to y, then the slot within it (RTL: rightmost first).
 */
function findInsertIndex(items: HandSlot[], x: number, y?: number): number {
  if (items.length === 0) return 0;
  let targetRow = 0;
  if (y !== undefined) {
    let best = Infinity;
    for (const it of items) {
      const d = Math.abs(it.centerY - y);
      if (d < best) { best = d; targetRow = it.row; }
    }
  }
  const before = items.filter((it) => it.row < targetRow).length;
  const rowItems = items.filter((it) => it.row === targetRow);
  for (let i = 0; i < rowItems.length; i++) {
    if (x > rowItems[i].centerX) return before + i;
  }
  return before + rowItems.length;
}

function updateDropIndicator(x: number, y?: number) {
  clearDragGap();

  const items = getVisualCardPositions();
  if (items.length === 0) return;

  const insertIdx = findInsertIndex(items, x, y);

  // Add gap indicator on the card AFTER the insertion point
  // insertIdx means: insert BEFORE items[insertIdx] in the sorted array.
  // The card at items[insertIdx] should show a gap on its right side (RTL: before it visually).
  // If insertIdx === items.length, the gap is after the leftmost card → mark leftmost card.
  if (insertIdx < items.length) {
    // Mark this card with gap on its right (appears to its visual right = before in array)
    const targetId = items[insertIdx].id;
    const el = document.querySelector(`[data-card-id="${targetId}"]`) as HTMLElement;
    if (el) el.classList.add("drag-gap");
  } else if (items.length > 0) {
    // After the leftmost card → mark last card
    const targetId = items[items.length - 1].id;
    const el = document.querySelector(`[data-card-id="${targetId}"]`) as HTMLElement;
    if (el) el.classList.add("drag-gap");
  }
}

function clearDragGap() {
  document.querySelectorAll(".drag-gap").forEach(el => el.classList.remove("drag-gap"));
}

function performDrop(x: number, y?: number) {
  const items = getVisualCardPositions();

  if (items.length === 0) {
    manualHandOrder = [dragState.cardId];
    return;
  }

  const insertIdx = findInsertIndex(items, x, y);

  // Rebuild manualHandOrder from current visual positions + inserted card
  const newOrder = items.map(it => it.id);
  newOrder.splice(insertIdx, 0, dragState.cardId);
  manualHandOrder = newOrder;
}

// Auto-scroll when dragging near edges of the hand scroll container
function startAutoScroll() {
  stopAutoScroll();
  const scrollEl = document.getElementById("hand-cards-scroll")!;
  dragScrollInterval = window.setInterval(() => {
    if (!dragState.active || !dragState.movedEnough) return;
    const rect = scrollEl.getBoundingClientRect();
    const edgeZone = 50;
    const x = dragState.currentX;
    if (x < rect.left + edgeZone) {
      // Near left edge → scroll left (in RTL this reveals cards on the right/end)
      scrollEl.scrollLeft -= 12;
    } else if (x > rect.right - edgeZone) {
      // Near right edge → scroll right
      scrollEl.scrollLeft += 12;
    }
  }, 30);
}

function stopAutoScroll() {
  if (dragScrollInterval !== null) {
    clearInterval(dragScrollInterval);
    dragScrollInterval = null;
  }
}

// --- Sort ---

function snapshotCurrentOrder() {
  if (!gameState) return;
  const me = gameState.players.find((p: any) => p.sessionId === mySessionId);
  if (!me) return;
  const visible = me.hand.filter((c: any) => !c.hidden);
  const sorted = sortHand(visible);
  manualHandOrder = sorted.map((c: any) => c.id);
}

function sortHand(cards: any[]): any[] {
  const arr = [...cards];
  if (handSortMode === "suit" || handSortMode === "manual") {
    arr.sort((a, b) => {
      const sd = (SUIT_SORT[a.suit] ?? 9) - (SUIT_SORT[b.suit] ?? 9);
      if (sd !== 0) return sd;
      return (RANK_SORT[a.rank] ?? 99) - (RANK_SORT[b.rank] ?? 99);
    });
  } else {
    arr.sort((a, b) => {
      const rd = (RANK_SORT[a.rank] ?? 99) - (RANK_SORT[b.rank] ?? 99);
      if (rd !== 0) return rd;
      return (SUIT_SORT[a.suit] ?? 9) - (SUIT_SORT[b.suit] ?? 9);
    });
  }
  return arr;
}

// --- Card Elements ---

/** Classic deck-joker face: vertical JOKER index + the jester symbol defined in index.html. */
const JOKER_FACE_HTML =
  '<span class="card-jk">JOKER</span><svg class="card-jh" aria-label="جوكر"><use href="#jester"/></svg>';

function createMiniCardEl(card: any): HTMLElement {
  const el = document.createElement("div");
  if (card.hidden || !card.rank) {
    el.className = "mini-card back";
    return el;
  }
  const isDeckJoker = card.isDeckJoker || card.rank === "joker";
  // Rank-2 joker looks like a normal card of its suit (no purple/joker styling)
  const colorClass = isDeckJoker ? "joker" : isRed(card.suit) ? "red" : "black";
  el.className = `mini-card ${colorClass}`;
  if (isDeckJoker) {
    el.innerHTML = JOKER_FACE_HTML;
    return el;
  }
  const sym = isDeckJoker ? "" : (SUIT_SYMBOLS[card.suit] || "");
  const displayRank = isDeckJoker ? "🃏" : card.rank;
  const centerContent = isDeckJoker ? `<span style="font-size:18px">🃏</span>` : sym;
  el.innerHTML = `
    <div class="mc-tl"><span>${displayRank}</span><span>${isDeckJoker ? "" : sym}</span></div>
    <div class="mc-center">${centerContent}</div>
    <div class="mc-br"><span>${displayRank}</span><span>${isDeckJoker ? "" : sym}</span></div>
  `;
  return el;
}

// Classic pip layout positions for each rank (positions in a 3-col x 5-row grid)
// Rows: 0=top, 1=upper-mid, 2=center, 3=lower-mid, 4=bottom
// Cols: 0=left, 1=center, 2=right
const PIP_LAYOUTS: Record<string, [number, number][]> = {
  "A":  [[2,1]],
  "2":  [[0,1],[4,1]],
  "3":  [[0,1],[2,1],[4,1]],
  "4":  [[0,0],[0,2],[4,0],[4,2]],
  "5":  [[0,0],[0,2],[2,1],[4,0],[4,2]],
  "6":  [[0,0],[0,2],[2,0],[2,2],[4,0],[4,2]],
  "7":  [[0,0],[0,2],[1,1],[2,0],[2,2],[4,0],[4,2]],
  "8":  [[0,0],[0,2],[1,1],[2,0],[2,2],[3,1],[4,0],[4,2]],
  "9":  [[0,0],[0,2],[1,0],[1,2],[2,1],[3,0],[3,2],[4,0],[4,2]],
  "10": [[0,0],[0,2],[1,0],[1,2],[1.5,1],[2.5,1],[3,0],[3,2],[4,0],[4,2]],
};

function buildPipGrid(sym: string, rank: string): string {
  const layout = PIP_LAYOUTS[rank];
  if (!layout) {
    // Face cards (J, Q, K) — show stylized rank letter with suit
    return `<div class="hc-pips"><div class="hc-face-symbol">${rank}</div><div class="hc-face-suit">${sym}</div></div>`;
  }
  let html = '<div class="hc-pips">';
  for (const [row, col] of layout) {
    // Convert grid position to percentage
    const top = 8 + (row / 4) * 84; // 8% to 92%
    const left = col === 0 ? 15 : col === 1 ? 50 : 85;
    const flip = row > 2 ? ' flipped' : '';
    html += `<span class="pip${flip}" style="top:${top}%;left:${left}%">${sym}</span>`;
  }
  html += '</div>';
  return html;
}

function createHandCardEl(card: any): HTMLElement {
  const el = document.createElement("div");
  // Guard against malformed card data
  if (!card || !card.rank || !card.suit) {
    el.className = "hand-card back";
    el.innerHTML = '<div class="hc-center" style="color:rgba(255,255,255,0.3);font-size:24px;">🂠</div>';
    if (card?.id) el.dataset.cardId = card.id;
    return el;
  }
  const isDeckJoker = card.isDeckJoker || card.rank === "joker";
  // Rank-2 joker: render as a normal card of its suit — only deck joker gets joker styling
  const colorClass = isDeckJoker ? "joker" : isRed(card.suit) ? "red" : "black";
  el.className = `hand-card ${colorClass}`;
  el.dataset.cardId = card.id;
  if (isDeckJoker) {
    el.innerHTML = JOKER_FACE_HTML;
    return el;
  }

  const sym = isDeckJoker ? "" : (SUIT_SYMBOLS[card.suit] || "");
  const displayRank = isDeckJoker ? "🃏" : card.rank;
  // Deck joker: show 🃏 face; rank-2 joker: normal pip layout for rank 2
  const jokerIndicator = isDeckJoker ? '<span class="joker-badge">🃏</span>' : '';
  const pipContent = isDeckJoker
    ? `<div class="hc-pips"><div class="hc-face-symbol deck-joker-face">🃏</div></div>`
    : buildPipGrid(sym, card.rank);

  el.innerHTML = `
    <div class="hc-tl"><span class="hc-rank">${isDeckJoker ? "★" : displayRank}</span><span class="hc-suit">${sym}</span></div>
    <div class="hc-center-mobile"><span style="font-size:${isDeckJoker ? "28px" : "24px"}">${isDeckJoker ? "🃏" : sym}</span></div>
    ${pipContent}${jokerIndicator}
    <div class="hc-br"><span class="hc-rank">${isDeckJoker ? "★" : displayRank}</span><span class="hc-suit">${sym}</span></div>
  `;
  return el;
}

// --- Client-side Meld Preview ---

function previewMeld(hand: any[]): string {
  const cards = hand.filter((c: any) => selectedCardIds.has(c.id));
  if (cards.length < 2) return "";

  const jokers = cards.filter((c: any) => c.isJoker);
  const nonJokers = cards.filter((c: any) => !c.isJoker);

  // All jokers
  // Sequence check — try A-low and A-high (no same-rank melds in Moor200)
  if (nonJokers.length > 0) {
    const suit = nonJokers[0].suit;
    const allSameSuit = nonJokers.every((c: any) => c.suit === suit);
    if (allSameSuit) {
      const sym = SUIT_SYMBOLS[suit] || suit;
      // Try both A-low and A-high
      const indicesLow = nonJokers.map((c: any) => SEQ_IDX[c.rank]).filter((i: any) => i !== undefined);
      const hasAce = nonJokers.some((c: any) => c.rank === "A");
      const indicesHigh = hasAce
        ? nonJokers.map((c: any) => c.rank === "A" ? 13 : SEQ_IDX[c.rank]).filter((i: any) => i !== undefined)
        : null;

      for (const raw of [indicesLow, ...(indicesHigh ? [indicesHigh] : [])]) {
        const indices = [...raw].sort((a: number, b: number) => a - b);
        if (indices.length === 0) continue;
        const minI = indices[0], maxI = indices[indices.length - 1];
        const span = maxI - minI + 1;
        const gaps = span - indices.length;
        if (gaps <= jokers.length) {
          const totalLen = span + (jokers.length - gaps);
          if (totalLen === cards.length && totalLen <= 14 && cards.length >= 3) {
            if (totalLen === 14) return `✓ مور1000 ${sym}`;
            if (totalLen >= 7) {
              const hasSubJ = jokers.some((c:any) => c.isDeckJoker || c.rank !== '2');
              return hasSubJ ? `✓ مور100 ${sym}` : `✓ مور200 ${sym}`;
            }
            return `✓ تسلسل ${sym} (${totalLen})`;
          }
        }
      }
      if (cards.length === 2) {
        return `… تسلسل ${sym} (${cards.length})`;
      }
    }
  }

  if (cards.length >= 3) return "✗ غير صالح";
  return `${cards.length} أوراق محددة`;
}

// --- Buttons ---

function updateButtons(state: any, me: any, isMyTurn: boolean) {
  const phase = state.phase;
  const tp = state.turnPhase;
  const selCount = selectedCardIds.size;

  const btnDraw = document.getElementById("draw-pile-btn") as HTMLElement;
  const btnTake = document.getElementById("btn-take-center") as HTMLButtonElement;
  const btnMeld = document.getElementById("btn-create-meld") as HTMLButtonElement;
  const btnAdd = document.getElementById("btn-add-to-meld") as HTMLButtonElement;
  const btnDiscard = document.getElementById("btn-discard") as HTMLButtonElement;
  const btnEnd = document.getElementById("btn-end-turn") as HTMLButtonElement;

  const canAct = phase === "playing" && isMyTurn && !actionPending;
  // Grace turn: player must take center first — block meld/add until they do
  const isGraceTurnStart = state.drawPileExhaustedSeat !== null &&
    state.drawPileExhaustedSeat !== undefined && tp === "start";

  btnDraw.style.opacity = canAct && tp === "start" && state.drawPile.length > 0 ? "1" : "0.4";
  btnDraw.style.cursor = canAct && tp === "start" && state.drawPile.length > 0 ? "pointer" : "default";
  (document.getElementById("btn-draw") as HTMLButtonElement).disabled =
    !(canAct && tp === "start" && state.drawPile.length > 0);
  btnTake.disabled = !(canAct && tp === "start" && canTakeCenter(state, me));
  const canMeldOrAdd = tp === "drawn" || (tp === "start" && state.hasTakenCenterThisTurn);
  btnMeld.disabled = !(canAct && !isGraceTurnStart && tp !== "took-initial-card" && canMeldOrAdd && selCount >= 3);
  btnAdd.disabled = !(canAct && !isGraceTurnStart && tp !== "took-initial-card" && canMeldOrAdd && selCount >= 1 && selectedMeldId);
  btnDiscard.disabled = !(canAct && (tp === "drawn" || tp === "must-discard" || tp === "took-initial-card") && selCount === 1);
  // Grace turn: allow ending turn (ends round) when draw pile exhausted or pile is 0.
  // The draw pile is sent hidden ({hidden:true}) so length=0 means truly empty.
  const drawPileEmpty = !state.drawPile || state.drawPile.length === 0;
  const canEndGraceTurn = (state.drawPileExhaustedSeat !== null && state.drawPileExhaustedSeat !== undefined) || drawPileEmpty;
  btnEnd.disabled = !(canAct && canEndGraceTurn);
  btnEnd.textContent = canEndGraceTurn ? "إنهاء الجولة ⏹" : "إنهاء ✓";

  // Took-center warning: single-card take requires melding before discard.
  const tookCenter = state.hasTakenCenterThisTurn;
  const melded = state.hasMeldedThisTurn;
  const mustMeldFirst = tookCenter && state.tookCenterCount === 1 && !melded;
  if (mustMeldFirst) {
    btnDiscard.disabled = true; // enforce — server also blocks it
  }

  // Meld preview indicator
  const previewEl = document.getElementById("meld-preview");
  if (previewEl) {
    const previewText = previewMeld(me.hand || []);
    previewEl.textContent = previewText;
    previewEl.className = previewText.startsWith("✓") ? "meld-preview valid"
      : previewText.startsWith("✗") ? "meld-preview invalid"
      : previewText.startsWith("…") ? "meld-preview partial"
      : "meld-preview";
  }
}

// --- Action handlers ---

function sendGameAction(action: any) {
  if (actionPending) return;
  actionPending = true;
  network.sendAction(action);
}

document.getElementById("draw-pile-btn")!.addEventListener("click", () => {
  if (!gameState || actionPending) return;
  const me = getMe();
  if (!me) return;
  if (gameState.phase !== "playing" || gameState.currentTurnSeat !== me.seatIndex) return;
  if (gameState.turnPhase !== "start" || gameState.drawPile.length === 0) return;
  sendGameAction({ type: "draw" });
});

// The "سحب" button in the action bar does the same as tapping the draw pile
document.getElementById("btn-draw")!.addEventListener("click", () => {
  document.getElementById("draw-pile-btn")!.click();
});

document.getElementById("btn-take-center")!.addEventListener("click", () => {
  if (!gameState || actionPending) return;
  const me = getMe();
  if (!me) return;
  if (gameState.phase !== "playing" || gameState.currentTurnSeat !== me.seatIndex) return;
  if (gameState.turnPhase !== "start" || !canTakeCenter(gameState, me)) return;

  // Moor200: take-center requires specifying the sequence you will lay down.
  // Include selected hand cards + the top center card.
  const topCard = getTopCenterCard(gameState);
  if (!topCard) return;

  // Initial center card: take it directly, no meld required
  if (gameState.isInitialCenterCard) {
    selectedCardIds.clear();
    sendGameAction({ type: "take-center", cardIds: [topCard.id] });
    return;
  }

  const selectedIds = Array.from(selectedCardIds);
  // Must include at least 2 hand cards + top card = 3 min for a sequence
  if (selectedIds.length < 2) {
    showNotification("حدد الأوراق من يدك التي ستشكل سلسلة مع الورقة الموجودة فوق الكومة");
    return;
  }
  const cardIds = [...selectedIds, topCard.id];
  selectedCardIds.clear();
  sendGameAction({ type: "take-center", cardIds });
});

document.getElementById("btn-create-meld")!.addEventListener("click", () => {
  if (actionPending) return;
  const ids = Array.from(selectedCardIds);
  if (ids.length >= 3) {
    sendGameAction({ type: "create-meld", cardIds: ids });
    selectedCardIds.clear();
  }
});

document.getElementById("btn-add-to-meld")!.addEventListener("click", () => {
  if (actionPending) return;
  const ids = Array.from(selectedCardIds);
  if (ids.length >= 1 && selectedMeldId) {
    sendGameAction({ type: "add-to-meld", cardIds: ids, meldId: selectedMeldId });
    selectedCardIds.clear();
    selectedMeldId = null;
  }
});

document.getElementById("btn-discard")!.addEventListener("click", () => {
  if (actionPending) return;
  const ids = Array.from(selectedCardIds);
  if (ids.length === 1) {
    sendGameAction({ type: "discard", cardId: ids[0] });
    selectedCardIds.clear();
  }
});

document.getElementById("btn-end-turn")!.addEventListener("click", () => {
  if (actionPending) return;
  sendGameAction({ type: "end-turn" });
});

// Sort buttons
document.getElementById("btn-sort-suit")!.addEventListener("click", () => {
  handSortMode = "suit";
  snapshotCurrentOrder();
  renderGame();
});
document.getElementById("btn-sort-rank")!.addEventListener("click", () => {
  handSortMode = "rank";
  renderGame();
});

// Side pile
document.getElementById("btn-side-a")!.addEventListener("click", () => {
  network.sendAction({ type: "choose-side-pile", pile: "A" });
});
document.getElementById("btn-side-b")!.addEventListener("click", () => {
  network.sendAction({ type: "choose-side-pile", pile: "B" });
});

// Restart
document.getElementById("btn-restart")!.addEventListener("click", () => {
  network.sendRestartGame();
  document.getElementById("game-over-overlay")!.classList.add("hidden");
  Object.keys(_lastHash).forEach(k => delete _lastHash[k]);
});

// New round (after round end)
document.getElementById("btn-new-round")!.addEventListener("click", () => {
  network.sendNewRound();
  document.getElementById("round-end-overlay")!.classList.add("hidden");
  Object.keys(_lastHash).forEach(k => delete _lastHash[k]);
});

// --- Round End Score Display ---

function showLastRoundResults() {
  if (!gameState?.roundScores?.length) return;
  const overlay = document.getElementById("round-end-overlay")!;
  const me = getMe();
  if (!me) return;
  renderRoundEndScores(gameState, me);
  // Change the button text to "إغلاق" instead of "الجولة التالية"
  const btn = document.getElementById("btn-new-round")!;
  btn.textContent = "إغلاق ✕";
  btn.onclick = () => {
    overlay.classList.add("hidden");
    // Restore the button for actual round-end use
    btn.textContent = "الجولة التالية ▶";
    btn.onclick = null;
  };
  overlay.classList.remove("hidden");
}

function renderRoundEndScores(state: any, me: any) {
  if (state.gameMode === '3p') {
    renderRoundEndScores3p(state);
    return;
  }
  const scores = state.roundScores;
  if (!scores || scores.length === 0) return;
  const lastRound = scores[scores.length - 1];

  // 4p: ensure correct panels visible
  const el4p = document.getElementById("round-end-4p");
  const el3p = document.getElementById("round-end-3p");
  if (el4p) el4p.style.display = "";
  if (el3p) el3p.style.display = "none";

  renderTeamScoreTable("score-table-a", lastRound.teamADetails, lastRound.teamAScore);
  renderTeamScoreTable("score-table-b", lastRound.teamBDetails, lastRound.teamBScore);

  document.getElementById("round-end-totals")!.innerHTML =
    `المجموع الكلي: <span style="color:var(--team-a); font-weight:900;">أ: ${state.teamATotalScore}</span> | ` +
    `<span style="color:var(--team-b); font-weight:900;">ب: ${state.teamBTotalScore}</span>`;
}

function renderRoundEndScores3p(state: any) {
  const scores = state.roundScores;
  if (!scores || scores.length === 0) return;
  const last = scores[scores.length - 1];

  // Show 3p section, hide 4p section
  const el4p = document.getElementById("round-end-4p");
  const el3p = document.getElementById("round-end-3p");
  if (el4p) el4p.style.display = "none";
  if (el3p) el3p.style.display = "block";

  const score3pCols = document.getElementById("score-3p-cols");
  if (!score3pCols) return;

  score3pCols.innerHTML = state.players.map((p: any) => {
    const seat = p.seatIndex as number;
    const d = last.playerDetails?.[seat] || {};
    const roundPts: number = last.playerScores?.[seat] ?? 0;
    const totalPts: number = state.playerTotalScores?.[seat] ?? 0;
    const role = state.soloPlayerSeat === seat ? "\u0633\u0648\u0644\u0648 \ud83d\udc51"
      : state.partnerSeats?.includes(seat) ? "\u0634\u0631\u064a\u0643 \ud83e\udd1d"
      : "\u0645\u0646\u0641\u0631\u062f";

    const bonusPts: number = d.meldBonusPoints ?? 0;
    const cardPts: number = d.meldCardPoints ?? 0;
    const penalty: number = d.cardsInHandPenalty ?? 0;
    const opened: number = d.openedBonus ?? 0;
    const sidePilePenalty: number = d.sidePileNotTakenPenalty ?? 0;
    const noMoor200Penalty: number = d.noMoor200Penalty ?? 0;

    const rows: { label: string; value: number; cls: string }[] = [
      { label: "\u0646\u0642\u0627\u0637 \u0645\u0648\u0631 (100/200/1000)", value: bonusPts, cls: bonusPts > 0 ? "positive" : "" },
      { label: "\u0642\u064a\u0645\u0629 \u0627\u0644\u0623\u0648\u0631\u0627\u0642 \u0639\u0644\u0649 \u0627\u0644\u0637\u0627\u0648\u0644\u0629", value: cardPts, cls: cardPts > 0 ? "positive" : "" },
      { label: "\u0645\u0643\u0627\u0641\u0623\u0629 \u0627\u0644\u0641\u062a\u062d", value: opened, cls: opened > 0 ? "positive" : "" },
      { label: "\u0623\u0648\u0631\u0627\u0642 \u0645\u062a\u0628\u0642\u064a\u0629 \u0641\u064a \u0627\u0644\u064a\u062f", value: penalty, cls: penalty < 0 ? "negative" : "" },
    ];
    if (sidePilePenalty < 0) rows.push({ label: "\u062e\u0635\u0645 \u0639\u062f\u0645 \u0623\u062e\u0630 \u0627\u0644\u0640\u061131", value: sidePilePenalty, cls: "negative" });
    if (noMoor200Penalty < 0) rows.push({ label: "\u062e\u0635\u0645 \u0639\u062f\u0645 \u0648\u062c\u0648\u062f \u0645\u0648\u0631200", value: noMoor200Penalty, cls: "negative" });

    const rowsHTML = rows.map(r => `
      <div class="score-row">
        <span class="label">${r.label}</span>
        <span class="value ${r.cls}">${r.value > 0 ? "+" : ""}${r.value}</span>
      </div>
    `).join("") + `
      <div class="score-row total">
        <span class="label">\u0627\u0644\u062c\u0648\u0644\u0629</span>
        <span class="value">${roundPts}</span>
      </div>
    `;

    return `
      <div class="score-team-col">
        <div class="score-team-header" style="color:var(--gold)">${escapeHTML(p.displayName)}</div>
        <div style="font-size:11px;color:#aaa;margin-bottom:4px;">${role}</div>
        <div class="score-table">${rowsHTML}</div>
        <div class="score-total-wrap"><span>\u0627\u0644\u0645\u062c\u0645\u0648\u0639 \u0627\u0644\u0643\u0644\u064a: ${totalPts}</span></div>
      </div>
    `;
  }).join("");
}

function renderTeamScoreTable(containerId: string, details: any, total: number) {
  const el = document.getElementById(containerId)!;
  const rows: {label:string;value:number;cls:string}[] = [];

  if ((details.meldBonusPoints ?? details.meldBasePoints) !== undefined) {
    // Moor200 scoring
    const bonusPts = details.meldBonusPoints ?? details.meldBasePoints ?? 0;
    const cardPts = details.meldCardPoints ?? details.cardCount ?? 0;
    const penalty = details.cardsInHandPenalty ?? 0;
    const opened = details.openedBonus ?? 0;
    const sidePilePenalty = details.sidePileNotTakenPenalty ?? 0;
    const noMoor200Penalty = details.noMoor200Penalty ?? 0;
    rows.push({ label: "نقاط مور (100/200/1000)", value: bonusPts, cls: bonusPts > 0 ? "positive" : "" });
    rows.push({ label: "قيمة الأوراق على الطاولة", value: cardPts, cls: cardPts > 0 ? "positive" : "" });
    rows.push({ label: "مكافأة الفتح", value: opened, cls: opened > 0 ? "positive" : "" });
    rows.push({ label: "أوراق متبقية في اليد", value: penalty, cls: penalty < 0 ? "negative" : "" });
    if (sidePilePenalty < 0) rows.push({ label: "خصم عدم أخذ الـ11", value: sidePilePenalty, cls: "negative" });
    if (noMoor200Penalty < 0) rows.push({ label: "خصم عدم وجود مور200", value: noMoor200Penalty, cls: "negative" });
  } else {
    // Fallback
    rows.push({ label: "نقاط", value: total, cls: total >= 0 ? "positive" : "negative" });
  }

  el.innerHTML = rows.map(r => `
    <div class="score-row">
      <span class="label">${r.label}</span>
      <span class="value ${r.cls}">${r.value > 0 ? "+" : ""}${r.value}</span>
    </div>
  `).join("") + `
    <div class="score-row total">
      <span class="label">المجموع</span>
      <span class="value">${total}</span>
    </div>
  `;
}

// --- Helpers ---

function getMe(): any {
  if (!gameState) return null;
  return gameState.players.find((p: any) => p.sessionId === mySessionId);
}

function showNotification(message: string) {
  const el = document.getElementById("notification")!;
  el.textContent = message;
  el.classList.remove("hidden");
  setTimeout(() => el.classList.add("hidden"), 3000);
}

function translateEvent(event: string, data?: any): string {
  const name = data?.playerName || "";
  const map: Record<string, string> = {
    "drew-card": "تم سحب ورقة",
    "game-started": "بدأت اللعبة!",
    "discarded": "تم رمي ورقة",
    "discarded-draw-exhausted": "⚠ كومة السحب فارغة — دور أخير للفريق!",
    "took-initial-card": "أخذت ورقة البداية 🎁 — ارمَ ورقة عوضاً عنها، ثم اسحب من كومة السحب",
    "discarded-initial-replacement": "تم الرمي — الآن اسحب من كومة السحب",
    "ended-turn": "انتهى الدور",
    "normalized-meld": "تم تحويل السلسلة لعادية",
    "added-to-meld": "تمت الإضافة للسلسلة",
    "new-round-started": "بدأت جولة جديدة",
    "game-restarted": "بدأت لعبة جديدة",
    "player-reconnected": `${name} عاد للعبة ✓`,
    "player-disconnected": `⚠ ${name} انقطع اتصاله`,
    "player-timeout": `${name} لم يعد — انتهى الوقت`,
    "player-kicked": `${name} تم طرده`,
    "players-reordered": `تم تبديل: ${name}`,
    "player-joined": `${name} انضم للغرفة`,
    "player-left": `${name} غادر الغرفة`,
    "spectator-joined": `${name} يشاهد 👁️`,
    "spectator-left": `${name} توقف عن المشاهدة`,
    "admin-message": `📢 ${name}`,
  };
  if (map[event]) return map[event];
  if (event.startsWith("took-center-")) return `تم أخذ ${event.split("-")[2]} ورقة`;
  if (event.startsWith("took-side-pile-")) return `تم أخذ الكومة الجانبية ${event.split("-")[3]}`;
  if (event.startsWith("created-meld-")) return "تم تنزيل سلسلة جديدة";
  if (event.startsWith("choosing-side-pile")) return "اختر كومة الـ 11";
  if (event.startsWith("game-over-winner-")) return `انتهت اللعبة! الفائز فريق ${event.split("-")[3]}`;
  if (event.startsWith("round-end")) return "انتهت الجولة";
  return event;
}
